import { INTAKE_EMAIL_BODY_DEFAULTS, INTAKE_REMINDER_EMAIL_BODY_DEFAULTS } from '@common/constants';
import type { ProspectMatterType } from '@common/enums';
import type { EmailTemplate } from '@common/types/lawfirms';
import type {
    FeeEstimate,
    FeeEstimatePayload,
    QuoteTemplateEntry,
    QuoteTemplatePricingUpdate,
    TemplateFormWithFields,
    ThresholdFee,
} from '@common/types/quoteTemplates';
import {
    type TemplateFormField,
    toTemplateFormFieldPayload,
} from '@common/types/templateFormFields';
import type { TemplateFormResponse } from '@common/types/templateForms';
import isEqual from 'lodash/isEqual';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { TabType } from '@/components/CommonTab';
import { DEFAULT_INTAKE_FORM_DESCRIPTION, DEFAULT_INTAKE_TEMPLATE_FOOTER } from '@/constants';
import axiosInstance from '@/services/axiosInstance';
import {
    createEmailTemplate,
    getPostIntakeTemplateInfo,
    updateEmailTemplate,
    type PostIntakeEmailTemplatePayloadInterface,
    updatePostIntakeTemplate,
} from '@/services/EmailService';
import {
    addNewTitleInsuranceEstimates,
    getTitleInsuranceEstimates,
} from '@/services/PricingDetailsService';
import {
    createIntakeByReference,
    createQuoteTemplate,
    type DynamicPriceCategoryType,
    type DynamicPriceType,
    fetchQuoteTemplateById,
    getDynamicPrices,
    updateQuoteIntakeTemplateStatus,
    updateQuoteTemplate,
    updateQuoteTemplatePricing,
} from '@/services/QuoteTemplatesService';
import {
    createTemplateForm,
    getIntakeTemplateForms,
    getQuoteTemplateForms,
    type Step4TemplateFormPayload,
    type TemplateFormWritePayload,
    updateTemplateForm,
} from '@/services/TemplateFormsService';
import { fetchUserById } from '@/services/UserService';
import { formatTimeSlots, validateNoThresholdOverlap } from '@/utils';

const defaultBodyBeforePricing = {
    Sale: 'Here is your personalized quote for the sale including all disbursements so you can see all costs and there are no surprises on closing.',
    Purchase:
        'Here is your personalized quote for the purchase including all disbursements so you can see all costs and there are no surprises on closing.',
    Refinance:
        'Here is your personalized quote for the refinancing including all disbursements so you can see all costs and there are no surprises on closing.',
    PrivateLender:
        'Here is your personalized quote for the private lender including all disbursements so you can see all costs and there are no surprises on closing.',
};
const defaultBodyBeforePricingReminder = {
    Sale: 'This is a reminder to review and accept the quote for legal services for the property sale you specified:',
    Purchase:
        'This is a reminder to review and accept the quote for legal services for the property purchase you specified:',
    Refinance:
        'This is a reminder to review and accept the quote for legal services for the property refinancing you specified:',
    PrivateLender:
        'This is a reminder to review and accept the quote for legal services related to the Private Lender transaction you specified.',
};

const getDefaultBodyBeforePricing = (matterType: ProspectMatterType, emailOrReminder: string) => {
    if (emailOrReminder === 'email') {
        return defaultBodyBeforePricing[matterType];
    }
    if (emailOrReminder === 'reminder') {
        return defaultBodyBeforePricingReminder[matterType];
    }
};

// Setup state is populated from a few quote-template endpoints, so it remains looser than
// the list response while still sharing the quote-template response shape.
interface ActiveQuote extends Partial<QuoteTemplateEntry> {
    quoteTemplateId: number;
    disabled?: boolean;
}

interface EmailReminderPayloadInterface {
    totalNumberOfReminders: number;
    emailFrequencyOfReminder: string;
    reminderTime: string;
}

export interface EmailTemplatePayloadInterface {
    emailTemplateType: string;
    isQuoteApprovalEnable: boolean;
    isQuoteReminderOn: boolean;
    emailSubject?: string | null;
    emailBodyIntroduction?: string | null;
    emailBodyClosing?: string | null;
    emailSignature?: string | null;
    emailCC?: string[] | null;
    emailReminder?: EmailReminderPayloadInterface | null;
    expiry?: number | null;
    enableReminderExhaustNotification?: boolean;
    disclaimerText?: string;
}

export type FormComponents = {
    title?: string;
    templateFormFieldId?: string;
    renderOptions?: {
        shouldWrap?: boolean;
        isRepeatable?: number; // if given then it'll repeat the wrapper component the given times
        targetComponent?: string; // based on this elements value we will repeat/map render this component
        wrapperComponent?: React.ReactNode | string; // In future if we have more than one type of wrapper component then we can pass the name here
    };
    templateFormFields?: TemplateFormField[]; // List of individual form elements
};

export type EstimationDynamicValue = {
    id?: number;
    description: string;
    amount: number;
    isAdded: boolean;
    isEditMode: boolean;
};

type TemplateFormApiResponse = TemplateFormWithFields | TemplateFormResponse;

export type InitialSetupStateType = {
    activeQuote: ActiveQuote; // This is the active quote template that is being created. by calling the POST API
    currentStep: number;
    loading: boolean;
    error: string | null;
    errorData: any;
    availableCcContacts: string[];
    calledStep1API: boolean;
    formData: {
        step0: {
            showError?: string;
            transactionType: ProspectMatterType;
            templateName: string;
            assignedLawyer: number;
            transactionTypePrevPayload: object;
            isIntakeCreated?: boolean;
            intakeFormStatus?: string;
            referencedIntakeOnlyQuoteTemplateId?: number | null;
            referencedIntakeOnlyQuoteTemplateName?: string | null;
            intakeFormAdded?: string | null;
            province: string | null;
            isPostIntakeEnable?: boolean;
        };
        step1: {
            templateFormId: string | null;
            quoteTemplateId: number | null;
            apiResponse: TemplateFormApiResponse | null;
            templateFormType: string;
            headerDescription: string;
            footerTemplate: string;
            templateFormFields: TemplateFormField[];
        };
        step2: {
            emailTemplateId?: number | null;
            emailTemplateType: string | null;
            emailReminderTemplateId?: number | null;
            isScreenTwo: boolean;
            subject: string;
            bodyBeforePricing: string;
            bodyAfterPricing: string;
            signatureOfEmail: string;
            contactsCC: string[];
            reminderSubject: string;
            reminderBodyBeforePricing: string;
            reminderBodyAfterPricing: string;
            reminderSignatureOfEmail: string;
            reminderContactsCC: string[];
            totalNumberOfReminders: number;
            emailFrequencyOfReminder: string;
            reminderTime: string;
            enableReminderExhaustNotification?: boolean;
            quoteEmailTemplatePrevPayload: object;
            quouteEmailReminderPrevPayload: object;
            isQuoteApprovalOn: boolean;
            isQuoteReminderOn: boolean;
            expiry?: number | null;
            disclaimerText?: string;
            reminderDisclaimerText?: string;
        };
        step3: {
            showWarning: string | null;
            showErrors: {
                missingValue: boolean | null;
                overlap: boolean | null;
                errorDetails: {
                    lessThan: object;
                    between: any;
                    greaterThan: object;
                };
            };
            dynamicFeesErrors: {
                missingValue: boolean | null;
                duplicateValue: boolean | null;
                dynamicHiddenFieldError: boolean | null;
                costHiddenFieldError: boolean | null;
                optionError: boolean | null;
                errorDetails: any;
            };
            isDisabled: boolean;
            isScreenTwo: boolean;
            isTransactionInToronto: string;
            pricingModel: 'Flat Fee' | 'Variable pricing' | '';
            flatFeeValue: string;
            legalFeeDiscount: number;
            // variablePricingValue: any
            greaterThanValue?: ThresholdFee;
            lessThanValue?: ThresholdFee;
            betweenValue: Array<ThresholdFee>;
            screenOnePayload: object;
            isDefaultDisbursementSet: boolean;
            isDefaultRegistrationSet: boolean;
            isDefaultTitleInsuranceSet: boolean;
            disbursementValues: EstimationDynamicValue[];
            disbursementApiValues: FeeEstimate[];
            registrationFeeValues: EstimationDynamicValue[];
            registrationFeeApiValues: FeeEstimate[];
            titleInsuranceEstimation: EstimationDynamicValue[];
            titleInsuranceApiValues: FeeEstimate[];
            //Keys for fee add on's configuration
            dynamicLegalFees: DynamicPriceType[];
            dynamicCostEstimationFields: DynamicPriceType[];
        };
        step4: {
            apiResponse: TemplateFormApiResponse | null;
            intakeFormDescription: string;
            textAfterSubmitIntakeForClient: string;
            quoteTemplateId?: number;
            templateFormId?: string;
            templateFormFields?: TemplateFormField[];
            currentIntakeTab: TabType;
            isScreenTwo: boolean;
            isRequiredForFullAutomation?: boolean;
        };
        step5: {
            isScreenTwo: boolean;
            subject: string;
            bodyBeforeLink: string;
            bodyAfterLink: string;
            signatureOfEmail: string;
            intakeEmailTemplateId?: number | null;
            isQuoteApprovalOn: boolean;
            contactCC: string[];
            intakeEmailStepOnePrevPayload: object;
            //intake reminder template keys
            intakeReminderEmailTemplateId?: number | null;
            reminderSubject: string;
            reminderBodyBeforeLink: string;
            reminderBodyAfterLink: string;
            reminderSignatureOfEmail: string;
            reminderContactsCC: string[];
            isQuoteReminderOn: boolean;
            totalNumberOfReminders: number;
            emailFrequencyOfReminder: string;
            reminderTime: string;
            intakeEmailTemplatePrevPayload: object;
            expiry?: number | null;
            enableReminderExhaustNotification?: boolean;
            //post intake template keys
            postIntakeSubject?: string;
            showWarning: string | null;
        };
    };
};

export const updateIntakeEmailReminderTemplate = async (
    payload: EmailTemplatePayloadInterface,
    quoteTemplateId: number,
    formData: InitialSetupStateType['formData'],
    errorData: any,
    setFormData: (data: any) => void,
    setError: (error: string | null) => void,
    setErrorData: (errorData: any) => void,
    setIsLoading: (isLoading: boolean) => void,
) => {
    if (!isEqual(payload, formData.step5.intakeEmailTemplatePrevPayload)) {
        setIsLoading(true);
        try {
            await updateEmailTemplate(
                quoteTemplateId,
                formData.step5.intakeReminderEmailTemplateId,
                payload,
            );
            setFormData({
                ...formData.step5,
                intakeEmailTemplatePrevPayload: payload,
            });
            setIsLoading(false);
        } catch (error: any) {
            setError('Please fill all required fields');
            setIsLoading(false);
            window.setTimeout(() => {
                setError(null);
            }, 5000);

            setErrorData({
                ...errorData,
                step5: {
                    ...errorData?.step5,
                    intakeReminderTemplateErrors: error?.response?.data || {},
                },
            });
            throw error;
        }
    }
};

const addIntakeEmailReminderTemplate = async (
    payload: EmailTemplatePayloadInterface,
    quoteTemplateId: number,
    formData: InitialSetupStateType['formData'],
    errorData: any,
    setFormData: (data: any) => void,
    setError: (error: string | null) => void,
    setErrorData: (errorData: any) => void,
    setIsLoading: (isLoading: boolean) => void,
) => {
    setIsLoading(true);
    try {
        const intakeEmailReminderResp = await createEmailTemplate(quoteTemplateId, payload);
        setFormData({
            ...formData.step5,
            intakeReminderEmailTemplateId: intakeEmailReminderResp.data?.emailTemplateId,
            intakeEmailTemplatePrevPayload: payload,
        });
        setIsLoading(false);
    } catch (error: any) {
        setIsLoading(false);
        if (error.status === 409) {
            const createdEmailTemplateId = error.response.data.emailTemplateId;
            setFormData({
                intakeReminderEmailTemplateId: createdEmailTemplateId,
            });
            if (createdEmailTemplateId) {
                const newFormData = {
                    ...formData,
                    step5: {
                        ...formData.step5,
                        intakeReminderEmailTemplateId: createdEmailTemplateId,
                    },
                };
                await updateIntakeEmailReminderTemplate(
                    payload,
                    quoteTemplateId,
                    newFormData,
                    errorData,
                    setFormData,
                    setError,
                    setErrorData,
                    setIsLoading,
                );
            }
        } else {
            setError('Please fill all required fields');
            window.setTimeout(() => {
                setError(null);
            }, 5000);

            setErrorData({
                ...errorData,
                step5: {
                    ...errorData?.step5,
                    intakeReminderTemplateErrors: error?.response?.data || {},
                },
            });

            throw error;
        }
    }
};

//Todo: Refactor to use same function for both intake email and intake reminder templates
const addIntakeEmailTemplate = async (
    payload: EmailTemplatePayloadInterface,
    quoteTemplateId: number,
    formData: InitialSetupStateType['formData'],
    errorData: any,
    setFormData: (data: any) => void,
    setError: (error: string | null) => void,
    setErrorData: (errorData: any) => void,
    setIsLoading: (isLoading: boolean) => void,
) => {
    setIsLoading(true);
    try {
        const intakeEmailTemplateResp = await axiosInstance.post(
            `/api/v1/quote-templates/${quoteTemplateId}/email-templates`,
            payload,
        );
        setFormData({
            ...formData.step5,
            isScreenTwo: true,
            intakeEmailStepOnePrevPayload: payload,
            intakeEmailTemplateId: intakeEmailTemplateResp.data?.emailTemplateId,
        });
        setIsLoading(false);
    } catch (error: any) {
        setIsLoading(false);
        if (error.status === 409) {
            const createdEmailTemplateId = error.response.data.emailTemplateId;
            setFormData({
                intakeEmailTemplateId: createdEmailTemplateId,
            });
            if (createdEmailTemplateId) {
                const newFormData = {
                    ...formData,
                    step5: {
                        ...formData.step5,
                        intakeEmailTemplateId: createdEmailTemplateId,
                    },
                };
                await updateIntakeEmailTemplate(
                    payload,
                    quoteTemplateId,
                    newFormData,
                    errorData,
                    setFormData,
                    setError,
                    setErrorData,
                    setIsLoading,
                );
            }
        } else {
            setError('Please fill all required fields');
            window.setTimeout(() => {
                setError(null);
            }, 5000);

            setErrorData({
                ...errorData,
                step5: {
                    ...errorData?.step5,
                    intakeEmailTemplateError: error?.response?.data || {},
                },
            });
            throw error;
        }
    }
};

//Todo: Refactor to use same function for both intake email and intake reminder templates
const updateIntakeEmailTemplate = async (
    payload: EmailTemplatePayloadInterface,
    quoteTemplateId: number,
    formData: any,
    errorData: any,
    setFormData: (data: any) => void,
    setError: (error: string | null) => void,
    setErrorData: (errorData: any) => void,
    setIsLoading: (isLoading: boolean) => void,
) => {
    if (!isEqual(payload, formData.step5.intakeEmailStepOnePrevPayload)) {
        setIsLoading(true);
        try {
            await updateEmailTemplate(
                quoteTemplateId,
                formData.step5.intakeEmailTemplateId,
                payload,
            );
            setFormData({
                ...formData.step5,
                isScreenTwo: true,
                intakeEmailStepOnePrevPayload: payload,
            });
            setIsLoading(false);
        } catch (error: any) {
            setError('Please fill all required fields');
            setIsLoading(false);
            window.setTimeout(() => {
                setError(null);
            }, 5000);

            setErrorData({
                ...errorData,
                step5: {
                    ...errorData?.step5,
                    intakeEmailTemplateError: error?.response?.data || {},
                },
            });
            throw error;
        }
    } else {
        setFormData({
            ...formData.step5,
            isScreenTwo: true,
        });
    }
};

const updateQuoteEmailTemplate = async (
    payload: EmailTemplatePayloadInterface,
    quoteTemplateId: number,
    formData: InitialSetupStateType['formData'],
    errorData: any,
    setFormData: (data: any) => void,
    setError: (error: string | null) => void,
    setErrorData: (errorData: any) => void,
    setIsLoading: (isLoading: boolean) => void,
) => {
    if (!isEqual(payload, formData.step2.quoteEmailTemplatePrevPayload)) {
        setIsLoading(true);
        try {
            await axiosInstance.put(
                `/api/v1/quote-templates/${quoteTemplateId}/email-templates/${formData.step2.emailTemplateId}`,
                payload,
            );
            setFormData({
                isScreenTwo: true,
                quoteEmailTemplatePrevPayload: payload,
            });
            setIsLoading(false);
        } catch (error: any) {
            console.error(error);
            setError('Please fill all required fields');
            setIsLoading(false);
            window.setTimeout(() => {
                setError(null);
            }, 5000);

            setErrorData({
                ...errorData,
                step2: {
                    ...errorData?.step2,
                    quoteEmailTemplateError: error?.response?.data || {},
                },
            });
            throw error;
        }
    } else {
        setFormData({
            isScreenTwo: true,
        });
    }
};

const addQuoteEmailTemplate = async (
    payload: EmailTemplatePayloadInterface,
    quoteTemplateId: number,
    formData: InitialSetupStateType['formData'],
    errorData: any,
    setFormData: (data: any) => void,
    setError: (error: string | null) => void,
    setErrorData: (errorData: any) => void,
    setIsLoading: (isLoading: boolean) => void,
) => {
    setIsLoading(true);
    try {
        const emailTemplateResp = await axiosInstance.post(
            `/api/v1/quote-templates/${quoteTemplateId}/email-templates`,
            payload,
        );
        setIsLoading(false);
        setFormData({
            isScreenTwo: true,
            emailTemplateId: emailTemplateResp.data?.emailTemplateId,
            quoteEmailTemplatePrevPayload: payload,
        });
    } catch (error: any) {
        console.error(error);
        setIsLoading(false);
        //If template already exists in database
        if (error.status === 409) {
            const createdEmailTemplateId = error.response.data.emailTemplateId;
            setFormData({
                emailTemplateId: createdEmailTemplateId,
            });
            if (createdEmailTemplateId) {
                const newFormData = {
                    ...formData,
                    step2: {
                        ...formData.step2,
                        emailTemplateId: createdEmailTemplateId,
                    },
                };
                await updateQuoteEmailTemplate(
                    payload,
                    quoteTemplateId,
                    newFormData,
                    errorData,
                    setFormData,
                    setError,
                    setErrorData,
                    setIsLoading,
                );
            }
        } else {
            setError('Please fill all required fields');
            window.setTimeout(() => {
                setError(null);
            }, 5000);

            setErrorData({
                ...errorData,
                step2: {
                    ...errorData?.step2,
                    quoteEmailTemplateError: error?.response?.data || {},
                },
            });
            throw error;
        }
    }
};

const updateQuoteReminderTemplate = async (
    payload: EmailTemplatePayloadInterface,
    quoteTemplateId: number,
    formData: InitialSetupStateType['formData'],
    errorData: any,
    setFormData: (data: any) => void,
    setError: (error: string | null) => void,
    setErrorData: (errorData: any) => void,
    setIsLoading: (isLoading: boolean) => void,
) => {
    if (!isEqual(payload, formData.step2.quouteEmailReminderPrevPayload)) {
        setIsLoading(true);
        try {
            await axiosInstance.put(
                `/api/v1/quote-templates/${quoteTemplateId}/email-templates/${formData.step2.emailReminderTemplateId}`,
                payload,
            );
            setFormData({
                ...formData.step2,
                quouteEmailReminderPrevPayload: payload,
            });
            setIsLoading(false);
        } catch (error: any) {
            console.error(error);
            setError('Please fill all required fields');
            setIsLoading(false);
            window.setTimeout(() => {
                setError(null);
            }, 5000);

            setErrorData({
                ...errorData,
                step2: {
                    ...errorData?.step2,
                    quouteRemindertemplateError: error?.response?.data || {},
                },
            });
            throw error;
        }
    }
};

const addQuoteEmailReminderTemplate = async (
    payload: EmailTemplatePayloadInterface,
    quoteTemplateId: number,
    formData: InitialSetupStateType['formData'],
    errorData: any,
    setFormData: (data: any) => void,
    setError: (error: string | null) => void,
    setErrorData: (errorData: any) => void,
    setIsLoading: (isLoading: boolean) => void,
) => {
    if (!isEqual(payload, formData.step2.quouteEmailReminderPrevPayload)) {
        setIsLoading(true);
        try {
            const emailReminderTemplateResp = await axiosInstance.post(
                `/api/v1/quote-templates/${quoteTemplateId}/email-templates`,
                payload,
            );
            setFormData({
                ...formData.step2,
                emailReminderTemplateId: emailReminderTemplateResp.data?.emailTemplateId || null,
                quouteEmailReminderPrevPayload: payload,
            });
            setIsLoading(false);
        } catch (error: any) {
            console.error(error);
            setIsLoading(false);
            //If template already exists in database
            if (error.status === 409) {
                const createdEmailTemplateId = error.response.data.emailTemplateId;
                setFormData({
                    emailReminderTemplateId: createdEmailTemplateId,
                });
                if (createdEmailTemplateId) {
                    const newFormData = {
                        ...formData,
                        step2: {
                            ...formData.step2,
                            emailReminderTemplateId: createdEmailTemplateId,
                        },
                    };
                    await updateQuoteReminderTemplate(
                        payload,
                        quoteTemplateId,
                        newFormData,
                        errorData,
                        setFormData,
                        setError,
                        setErrorData,
                        setIsLoading,
                    );
                }
            } else {
                setError('Please fill all required fields');
                window.setTimeout(() => {
                    setError(null);
                }, 5000);

                setErrorData({
                    ...errorData,
                    step2: {
                        ...errorData?.step2,
                        quouteRemindertemplateError: error?.response?.data || {},
                    },
                });
                throw error;
            }
        }
    }
};

const updatePostIntakeEmailTemplate = async (
    payload: PostIntakeEmailTemplatePayloadInterface,
    quoteTemplateId: number,
    formData: any,
    errorData: any,
    setFormData: (data: any) => void,
    setError: (error: string | null) => void,
    setErrorData: (errorData: any) => void,
    setIsLoading: (isLoading: boolean) => void,
) => {
    setIsLoading(true);
    try {
        await updatePostIntakeTemplate(quoteTemplateId, payload);
        setFormData({
            ...formData.step5,
            isScreenTwo: true,
        });
        setIsLoading(false);
    } catch (error: any) {
        setError('Please fill all required fields');
        setFormData({
            ...formData.step5,
            isScreenTwo: false,
        });
        setIsLoading(false);
        window.setTimeout(() => {
            setError(null);
        }, 5000);

        setErrorData({
            ...errorData,
            step5: {
                ...errorData?.step5,
                postIntakeEmailTemplateError: error?.response?.data || undefined,
            },
        });
        throw error;
    }
};

const initialState: InitialSetupStateType = {
    activeQuote: {} as ActiveQuote,
    currentStep: 0,
    loading: false,
    error: null,
    errorData: {
        step0: {
            process: undefined,
            quoteTemplateName: undefined,
            quoteApproverId: undefined,
            isPostIntakeEnable: false,
        },
        step2: {
            quoteEmailTemplateError: {
                emailSubject: undefined,
                emailSignature: undefined,
                emailBodyClosing: undefined,
                emailBodyIntroduction: undefined,
            },
            quouteRemindertemplateError: {
                emailSubject: undefined,
                emailSignature: undefined,
                emailBodyClosing: undefined,
                emailBodyIntroduction: undefined,
            },
        },
        step5: {
            intakeEmailTemplateError: {
                emailSubject: undefined,
                emailSignature: undefined,
                emailBodyClosing: undefined,
                emailBodyIntroduction: undefined,
            },
            intakeReminderTemplateErrors: {
                emailSubject: undefined,
                emailSignature: undefined,
                emailBodyClosing: undefined,
                emailBodyIntroduction: undefined,
            },
        },
    },
    calledStep1API: false,
    availableCcContacts: [],
    formData: {
        step0: {
            showError: undefined,
            transactionType: '',
            templateName: '',
            assignedLawyer: 0,
            transactionTypePrevPayload: {},
            intakeFormAdded: '',
            province: '',
        },
        step1: {
            apiResponse: null,
            headerDescription: '',
            footerTemplate: '',
            quoteTemplateId: null,
            templateFormId: null,
            templateFormType: 'QuoteForm',
            templateFormFields: [],
        },
        step2: {
            emailTemplateId: null,
            emailTemplateType: null,
            emailReminderTemplateId: null,
            isScreenTwo: false,
            subject: 'Quote email',
            bodyBeforePricing: '',
            bodyAfterPricing:
                'Above are the total costs associated with your transaction. Let me know if you have any questions. If you would like to proceed, please click the "Accept" button and you will receive an email link to our intake form.',
            signatureOfEmail: '',
            contactsCC: [],
            reminderSubject: 'Quote email reminder',
            reminderBodyBeforePricing: '',
            reminderBodyAfterPricing:
                'Above are the total costs associated with your transaction. Let me know if you have any questions. If you would like to proceed, please click the "Accept" button and you will receive an email link to our intake form.',
            reminderSignatureOfEmail: '',
            reminderContactsCC: [],
            totalNumberOfReminders: 2,
            emailFrequencyOfReminder: 'EverySecondDay',
            reminderTime: 'Morning(09:00 AM)',
            quoteEmailTemplatePrevPayload: {},
            quouteEmailReminderPrevPayload: {},
            isQuoteApprovalOn: false,
            isQuoteReminderOn: true,
            expiry: 180,
            enableReminderExhaustNotification: true,
            disclaimerText: '',
            reminderDisclaimerText: '',
        },
        step3: {
            showWarning: null,
            showErrors: {
                missingValue: null,
                overlap: null,
                errorDetails: {
                    lessThan: {},
                    between: [],
                    greaterThan: {},
                },
            },
            dynamicFeesErrors: {
                missingValue: null,
                duplicateValue: null,
                dynamicHiddenFieldError: null,
                costHiddenFieldError: null,
                optionError: null,
                errorDetails: [],
            },
            isDisabled: false,
            isScreenTwo: false,
            isTransactionInToronto: '',
            pricingModel: '',
            flatFeeValue: '',
            legalFeeDiscount: 0,
            greaterThanValue: {
                operatorType: 'greaterThan',
                value: [1500000],
                legalFee: 1200,
            },
            lessThanValue: {
                operatorType: 'lessThan',
                value: [500000],
                legalFee: 800,
            },
            betweenValue: [
                {
                    operatorType: 'between',
                    value: [500000, 1500000],
                    legalFee: 1000,
                },
            ],
            screenOnePayload: {},
            isDefaultDisbursementSet: false,
            isDefaultRegistrationSet: false,
            isDefaultTitleInsuranceSet: false,
            disbursementValues: [],
            disbursementApiValues: [],
            registrationFeeValues: [],
            registrationFeeApiValues: [],
            titleInsuranceEstimation: [],
            titleInsuranceApiValues: [],
            dynamicLegalFees: [],
            dynamicCostEstimationFields: [],
        },
        step4: {
            apiResponse: null,
            isScreenTwo: false,
            intakeFormDescription: '',
            textAfterSubmitIntakeForClient: '',
            quoteTemplateId: undefined,
            templateFormId: undefined,
            templateFormFields: [],
            currentIntakeTab: {
                tab: 0,
                tabLabel: 'Initial details',
            },
        },
        step5: {
            isScreenTwo: false,
            subject: INTAKE_EMAIL_BODY_DEFAULTS.Sale.subject,
            bodyBeforeLink: INTAKE_EMAIL_BODY_DEFAULTS.Sale.bodyBeforeLink,
            bodyAfterLink: INTAKE_EMAIL_BODY_DEFAULTS.Sale.bodyAfterLink,
            signatureOfEmail: '',
            isQuoteApprovalOn: false,
            intakeEmailTemplateId: null,
            contactCC: [],
            intakeEmailStepOnePrevPayload: {},
            // intake reminder keys
            intakeReminderEmailTemplateId: null,
            intakeEmailTemplatePrevPayload: {},
            reminderSubject: INTAKE_REMINDER_EMAIL_BODY_DEFAULTS.Sale.subject,
            reminderBodyBeforeLink: INTAKE_REMINDER_EMAIL_BODY_DEFAULTS.Sale.bodyBeforeLink,
            reminderBodyAfterLink: INTAKE_REMINDER_EMAIL_BODY_DEFAULTS.Sale.bodyAfterLink,
            reminderSignatureOfEmail: '',
            reminderContactsCC: [],
            isQuoteReminderOn: true,
            emailFrequencyOfReminder: 'EveryThirdDay',
            reminderTime: 'Morning(09:00 AM)',
            totalNumberOfReminders: 3,
            expiry: 180,
            enableReminderExhaustNotification: true,
            postIntakeSubject: '',
            showWarning: null,
        },
    },
};

type SetupActionsType = {
    updateStep1AfterReorder: (data: any) => void;
    updateStep4IntakeAfterReorder: (data: any) => void;
    setDefaultTemplateFormFields: (matterName: string) => void;
    setCurrentStep: (step: number) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    setErrorData: (errorData: object) => void;
    setFormData: (
        step: 'step0' | 'step1' | 'step2' | 'step3' | 'step4' | 'step5',
        data: object,
    ) => void;
    setDynamicPricing: (
        dynamicPriceCategory: DynamicPriceCategoryType,
        key: keyof DynamicPriceType,
        index: number,
        value: any,
    ) => void;
    // fetchQuoteTemplateData: (quoteIntakeId?: string) => Promise<void>
    fetchQuoteTemplateDataOnEveryStep: (
        step: 'step0' | 'step1' | 'step2' | 'step3' | 'step4' | 'step5',
        quoteIntakeId?: string | number,
        subStep?: '1' | '2',
        isCalledFromStep1?: boolean,
    ) => Promise<void>;
    preparePayloadForStep1: () => TemplateFormWritePayload;
    preparePayloadForStep4: () => Step4TemplateFormPayload;
    callApi: (step: string, subStep?: string) => Promise<void>;
    resetState: () => void;
    resetSetupState: () => void;
    setCcContact: (ccContacts: string[]) => void;
    setCostEstimationsValues: (
        apiValueKey:
            | 'disbursementApiValues'
            | 'registrationFeeApiValues'
            | 'disbursementValues'
            | 'registrationFeeValues'
            | 'isDefaultDisbursementSet'
            | 'isDefaultRegistrationSet'
            | 'titleInsuranceEstimation'
            | 'titleInsuranceApiValues'
            | 'isDefaultTitleInsuranceSet',
        value: any,
    ) => void;
    setLawyerSignature: (
        userId: number,
        quoteIntake?: string | number | undefined,
    ) => Promise<void>;
    resetStep3Errors: () => void;
};

const useQuoteIntakeSetupStore = create(
    immer<InitialSetupStateType & SetupActionsType>((set, get) => ({
        ...initialState,
        updateStep1AfterReorder: (resp) => {
            set((state) => {
                state.formData.step1.templateFormFields = resp?.data?.templateFormFields ?? [];
            });
        },
        updateStep4IntakeAfterReorder: (resp) => {
            set((state) => {
                state.formData.step4.templateFormFields = resp.data.templateFormFields;
            });
        },
        setDefaultTemplateFormFields: (matterName) => {
            set((state) => {
                state.formData.step2.bodyBeforePricing = defaultBodyBeforePricing[matterName];
                state.formData.step2.reminderBodyBeforePricing =
                    defaultBodyBeforePricingReminder[matterName];
                state.formData.step4.intakeFormDescription = DEFAULT_INTAKE_FORM_DESCRIPTION;
                state.formData.step4.textAfterSubmitIntakeForClient =
                    DEFAULT_INTAKE_TEMPLATE_FOOTER;
            });
        },
        setCurrentStep: (step) =>
            set((state) => {
                state.currentStep = step;
            }),
        setLoading: (loading) =>
            set((state) => {
                state.loading = loading;
            }),
        setError: (error) =>
            set((state) => {
                state.error = error;
            }),
        setErrorData: (errorData: object) =>
            set((state) => {
                state.errorData = errorData;
            }),
        setFormData: (step: 'step0' | 'step1' | 'step2' | 'step3' | 'step4' | 'step5', data: any) =>
            set((state) => {
                state.formData[step] = {
                    ...state.formData[step],
                    ...data,
                };
            }),
        setDynamicPricing: (
            dynamicPriceCategory: DynamicPriceCategoryType,
            key: keyof DynamicPriceType,
            index: number,
            value: any,
        ) => {
            switch (dynamicPriceCategory) {
                case 'LegalFee':
                    set((state) => {
                        state.formData.step3.dynamicLegalFees[index][key] = value;
                    });
                    if (key === 'question') {
                        set((state) => {
                            state.formData.step3.dynamicLegalFees[index].answer = '';
                        });
                    }
                    break;

                case 'RegistrationFee':
                case 'DisbursementFee':
                    set((state) => {
                        state.formData.step3.dynamicCostEstimationFields[index][key] = value;
                    });
                    if (key === 'question') {
                        set((state) => {
                            state.formData.step3.dynamicCostEstimationFields[index].answer = '';
                        });
                    }
                    break;

                default:
                    return;
            }
        },
        preparePayloadForStep1: (): TemplateFormWritePayload => ({
            templateFormType: 'QuoteForm',
            headerDescription: get().formData.step1.headerDescription,
            footerTemplate: get().formData.step1.footerTemplate,
            templateFormFields: get().formData.step1.templateFormFields.map(
                toTemplateFormFieldPayload,
            ),
        }),
        preparePayloadForStep4: (): Step4TemplateFormPayload => ({
            templateFormType: 'IntakeForm',
            headerDescription: get().formData.step4.intakeFormDescription,
            footerTemplate: get().formData.step4.textAfterSubmitIntakeForClient,
            quoteTemplateId: get().formData.step4.quoteTemplateId,
            templateFormId: get().formData.step4.templateFormId,
            templateFormFields:
                get().formData.step4.templateFormFields?.map(toTemplateFormFieldPayload) ?? [],
        }),
        callApi: async (step: string, subStep?: string) => {
            if (step === 'step0') {
                // prepare the payload
                const payload = {
                    process: get().formData.step0.transactionType,
                    quoteTemplateName: get().formData.step0.templateName,
                    quoteApproverId: get().formData.step0.assignedLawyer,
                    province: get().formData.step0.province,
                };
                //if activeQuote id is present then call put API else call post api.
                if (!isEqual(payload, get().formData.step0.transactionTypePrevPayload)) {
                    set((state) => {
                        state.loading = true;
                    });
                    if (get()?.activeQuote?.quoteTemplateId) {
                        await updateQuoteTemplate(get()?.activeQuote?.quoteTemplateId, payload)
                            // Change the endpoint and method accordingly
                            .then((response) => {
                                // Handle success
                                // set(() => ({ loading: false, data: response.data })) // Assuming you might want to store data in Zustand
                                // if successful then set the next step and set the id of the quote template
                                set((state) => {
                                    state.loading = false;
                                    state.currentStep = state.currentStep + 1;
                                    state.activeQuote = response.data;
                                    state.formData.step0.transactionTypePrevPayload = payload;
                                });
                            })
                            .catch((error) => {
                                set((state) => {
                                    state.loading = false;
                                    state.formData.step0.showError =
                                        error?.response?.data || 'Something went wrong!';
                                    state.errorData.step0 = error?.response?.data || {};
                                });
                                // clear showError
                                window.setTimeout(() => {
                                    set((state) => {
                                        state.formData.step0.showError = undefined;
                                    });
                                }, 5000);
                            });
                    } else {
                        await createQuoteTemplate(payload)
                            .then((response) => {
                                // Handle success
                                // set(() => ({ loading: false, data: response.data })) // Assuming you might want to store data in Zustand
                                // if successful then set the next step and set the id of the quote template
                                set((state) => {
                                    state.loading = false;
                                    state.currentStep = state.currentStep + 1;
                                    state.activeQuote = response.data;
                                });
                            })
                            .catch((error) => {
                                set((state) => {
                                    state.loading = false;
                                    state.errorData.step0 = error?.response?.data || {};
                                    state.formData.step0.showError =
                                        error?.response?.data || 'Something went wrong!';
                                });

                                window.setTimeout(() => {
                                    set((state) => {
                                        state.formData.step0.showError = undefined;
                                    });
                                }, 5000);
                            });
                    }
                } else {
                    set((state) => {
                        state.loading = false;
                        state.currentStep = state.currentStep + 1;
                    });
                }
            }
            if (step === 'step1') {
                //validate description length
                if (get().formData.step1.headerDescription.length > 4000) {
                    return;
                }

                // validate footerTemplate length
                if (get().formData.step1.footerTemplate.length > 10000) {
                    return;
                }

                const apiResponse = get().formData.step1.apiResponse;

                const payload = get().preparePayloadForStep1();

                if (apiResponse) {
                    //Call the update

                    const {
                        templateFormId: _templateFormId,
                        quoteTemplateId: _quoteTemplateId,
                        templateFormFields: _templateFormFields,
                        ...prevPayload
                    } = apiResponse;

                    const newPayload = {
                        headerDescription: payload.headerDescription,
                        footerTemplate: payload.footerTemplate,
                        templateFormType: payload.templateFormType,
                    };

                    if (!isEqual(prevPayload, newPayload)) {
                        if (get().formData.step1.apiResponse?.templateFormId !== undefined) {
                            set((state) => {
                                state.loading = true;
                            });
                            // creating payload for update
                            try {
                                const quoteTemplateFormResp = await updateTemplateForm(
                                    get().activeQuote.quoteTemplateId,
                                    get().formData.step1.apiResponse?.templateFormId,
                                    payload,
                                );
                                set((state) => {
                                    state.loading = false;
                                    state.currentStep = state.currentStep + 1;
                                    state.formData.step1.apiResponse = quoteTemplateFormResp.data;
                                    state.formData.step1.footerTemplate =
                                        quoteTemplateFormResp.data.footerTemplate ?? '';
                                    state.formData.step1.headerDescription =
                                        quoteTemplateFormResp.data.headerDescription ?? '';
                                    state.formData.step1.quoteTemplateId =
                                        quoteTemplateFormResp.data.quoteTemplateId;
                                    state.formData.step1.templateFormId =
                                        quoteTemplateFormResp.data.templateFormId;
                                    state.formData.step1.templateFormFields =
                                        quoteTemplateFormResp.data.templateFormFields;
                                });
                            } catch (error: any) {
                                set((state) => {
                                    state.error = error.response?.data || 'Something went wrong!';
                                    state.loading = false;
                                });
                                const timeout = window.setTimeout(() => {
                                    set((state) => {
                                        state.error = null;
                                        state.loading = false;
                                    });

                                    window.clearTimeout(timeout);
                                }, 5000);
                            }
                        } else {
                            set((state) => {
                                state.loading = false;
                                state.currentStep = state.currentStep + 1;
                            });
                        }
                    }
                } else {
                    //Call Create quote template form api
                    try {
                        set((state) => {
                            state.loading = true;
                        });
                        const templateFormResp = await createTemplateForm(
                            get().activeQuote.quoteTemplateId,
                            payload,
                        );
                        set((state) => {
                            state.loading = false;
                            state.currentStep = state.currentStep + 1;
                            state.formData.step1.apiResponse = templateFormResp.data;
                            state.formData.step1.footerTemplate =
                                templateFormResp.data.footerTemplate ?? '';
                            state.formData.step1.headerDescription =
                                templateFormResp.data.headerDescription ?? '';
                            state.formData.step1.quoteTemplateId =
                                templateFormResp.data.quoteTemplateId;
                            state.formData.step1.templateFormId =
                                templateFormResp.data.templateFormId;
                            state.formData.step1.templateFormFields =
                                templateFormResp.data.templateFormFields;
                        });
                    } catch (error: any) {
                        set((state) => {
                            state.error = error.response?.data || 'Something went wrong!';
                            state.loading = false;
                        });
                        const timeout = window.setTimeout(() => {
                            set((state) => {
                                state.error = null;
                            });

                            window.clearTimeout(timeout);
                        }, 5000);
                    }
                }
            }

            if (step === 'step2' && subStep === '1') {
                const payload: EmailTemplatePayloadInterface = {
                    emailTemplateType: 'QuoteEmailTemplate',
                    isQuoteApprovalEnable: get().formData.step2.isQuoteApprovalOn,
                    isQuoteReminderOn: false,
                    emailSubject: get().formData.step2.subject,
                    emailBodyIntroduction: get().formData.step2.bodyBeforePricing,
                    emailBodyClosing: get().formData.step2.bodyAfterPricing,
                    emailSignature: get().formData.step2.signatureOfEmail,
                    emailCC: get().formData.step2.contactsCC,
                    emailReminder: null,
                    expiry: get().formData.step2.expiry,
                    disclaimerText: get().formData.step2.disclaimerText,
                };
                // call the API to update the email template
                // activeQuote is a dependency it needs to have the quoteTemplateId
                if (get().formData.step2.emailTemplateId) {
                    await updateQuoteEmailTemplate(
                        payload,
                        get().activeQuote?.quoteTemplateId,
                        get().formData,
                        get().errorData,
                        (data: any) => {
                            set((state) => {
                                state.formData.step2 = {
                                    ...state.formData.step2,
                                    ...data,
                                };
                            });
                        },
                        (error: string | null) =>
                            set((_state) => ({
                                error: error,
                            })),
                        (errorData: any) =>
                            set((_state) => ({
                                errorData: errorData,
                            })),
                        (isLoading: boolean) =>
                            set((state) => {
                                state.loading = isLoading;
                            }),
                    );
                } else {
                    await addQuoteEmailTemplate(
                        payload,
                        get().activeQuote?.quoteTemplateId,
                        get().formData,
                        get().errorData,
                        (data: any) => {
                            set((state) => {
                                state.formData.step2 = {
                                    ...state.formData.step2,
                                    ...data,
                                };
                            });
                        },
                        (error: string | null) =>
                            set((_state) => ({
                                error: error,
                            })),
                        (errorData: any) =>
                            set((_state) => ({
                                errorData: errorData,
                            })),
                        (isLoading: boolean) =>
                            set((state) => {
                                state.loading = isLoading;
                            }),
                    );
                }
            } else if (step === 'step2' && subStep === '2') {
                // for email reminderr template
                const payload: EmailTemplatePayloadInterface = {
                    emailTemplateType: 'QuoteEmailReminderTemplate',
                    isQuoteApprovalEnable: false,
                    isQuoteReminderOn: get().formData.step2.isQuoteReminderOn,
                    emailSubject: get().formData.step2.reminderSubject,
                    emailBodyIntroduction: get().formData.step2.reminderBodyBeforePricing,
                    emailBodyClosing: get().formData.step2.reminderBodyAfterPricing,
                    emailSignature: get().formData.step2.reminderSignatureOfEmail,
                    emailCC: get().formData.step2.reminderContactsCC,
                    emailReminder: {
                        totalNumberOfReminders: get().formData.step2.totalNumberOfReminders,
                        emailFrequencyOfReminder: get().formData.step2.emailFrequencyOfReminder,
                        reminderTime: get().formData.step2.reminderTime,
                    },
                    enableReminderExhaustNotification:
                        get().formData.step2.enableReminderExhaustNotification || false,
                    disclaimerText: get().formData.step2.reminderDisclaimerText,
                };
                // call the API to update the email template
                // activeQuote is a dependency it needs to have the quoteTemplateId
                if (get().formData.step2.emailReminderTemplateId) {
                    await updateQuoteReminderTemplate(
                        payload,
                        get().activeQuote?.quoteTemplateId,
                        get().formData,
                        get().errorData,
                        (data: any) => {
                            set((state) => ({
                                ...state,
                                formData: {
                                    ...state.formData,
                                    step2: {
                                        ...state.formData.step2,
                                        ...data,
                                    },
                                },
                            }));
                        },
                        (error: string | null) =>
                            set((_state) => ({
                                error: error,
                            })),
                        (errorData: any) =>
                            set((_state) => ({
                                errorData: errorData,
                            })),
                        (isLoading: boolean) =>
                            set((state) => {
                                state.loading = isLoading;
                            }),
                    );
                } else {
                    await addQuoteEmailReminderTemplate(
                        payload,
                        get().activeQuote?.quoteTemplateId,
                        get().formData,
                        get().errorData,
                        (data: any) => {
                            set((state) => ({
                                ...state,
                                formData: {
                                    ...state.formData,
                                    step2: {
                                        ...state.formData.step2,
                                        ...data,
                                    },
                                },
                            }));
                        },
                        (error: string | null) =>
                            set((_state) => ({
                                error: error,
                            })),
                        (errorData: any) =>
                            set((_state) => ({
                                errorData: errorData,
                            })),
                        (isLoading: boolean) =>
                            set((state) => {
                                state.loading = isLoading;
                            }),
                    );
                }
            }

            if (step === 'step3' && subStep === '1') {
                const pricingModel = get().formData.step3.pricingModel;
                let thresholdPayload: ThresholdFee[] = [];
                const lessThanValuesObj =
                    get().formData.step3.lessThanValue || ({} as ThresholdFee);
                const betweenValuesArr =
                    get().formData.step3.betweenValue || ([] as ThresholdFee[]);
                const greaterThanValuesObj =
                    get().formData.step3.greaterThanValue || ({} as ThresholdFee);
                thresholdPayload = [lessThanValuesObj, ...betweenValuesArr, greaterThanValuesObj];
                //reset errors
                set((state) => {
                    state.formData.step3.showErrors = {
                        missingValue: false,
                        overlap: false,
                        errorDetails: {
                            lessThan: {},
                            between: [],
                            greaterThan: {},
                        },
                    };
                    state.formData.step3.dynamicFeesErrors = {
                        ...state.formData.step3.dynamicFeesErrors,
                        missingValue: null,
                        duplicateValue: null,
                        errorDetails: [],
                    };
                });
                // remove the commas from the legalFee and amount
                const updatedThresholdPayload = thresholdPayload.map(
                    (item: any, _index: number) => {
                        const tempLegalFee = Number(item.legalFee?.toString().replace(/,/g, ''));
                        if (item.value === 0) {
                            set((state) => {
                                state.formData.step3.showErrors.missingValue = true;
                            });
                            return;
                        }
                        const tempValue = item.value.map((value: any) =>
                            value ? Number(value.toString().replace(/,/g, '')) : null,
                        );
                        return {
                            ...item,
                            legalFee: tempLegalFee,
                            value: tempValue,
                        };
                    },
                );
                const payload: QuoteTemplatePricingUpdate = {
                    isConductTransactionInToronto: Boolean(
                        get().formData.step3.isTransactionInToronto === 'Yes',
                    ),
                    flatFee:
                        pricingModel === 'Flat Fee' && get().formData.step3.flatFeeValue
                            ? get().formData.step3.flatFeeValue.replace(/,/g, '')
                            : '0',
                    thresholdFee: updatedThresholdPayload,
                    pricingModel: pricingModel === 'Flat Fee' ? 'FlatFee' : 'Threshold',
                    legalFeeDiscount: get().formData.step3.legalFeeDiscount || 0,
                };

                if (pricingModel === '') {
                    set((state) => {
                        state.formData.step3.showWarning = 'Please select a pricing model.';
                    });
                    const timer = window.setTimeout(() => {
                        set((state) => {
                            state.formData.step3.showWarning = null;
                        });

                        window.clearTimeout(timer);
                    }, 3000);
                    return;
                }

                if (pricingModel === 'Variable pricing') {
                    let hasEmptyFields = false;
                    updatedThresholdPayload.forEach((item, thresholdIndex: number) => {
                        switch (item.operatorType) {
                            case 'lessThan': {
                                if (!item.legalFee || item.legalFee === 0) {
                                    set((state) => {
                                        state.formData.step3.showErrors.errorDetails.lessThan = {
                                            field: 'legalFee',
                                            error: true,
                                        };
                                    });
                                    hasEmptyFields = true;
                                }
                                if (item.value?.length === 0) {
                                    set((state) => {
                                        state.formData.step3.showErrors.errorDetails.lessThan = {
                                            field: 'amount',
                                            error: true,
                                        };
                                    });
                                    hasEmptyFields = true;
                                }
                                if (item.value?.length > 0 && !item.value[0]) {
                                    set((state) => {
                                        state.formData.step3.showErrors.errorDetails.lessThan = {
                                            field: 'amount',
                                            error: true,
                                        };
                                    });
                                    hasEmptyFields = true;
                                }
                                break;
                            }
                            case 'greaterThan': {
                                if (!item.legalFee || item.legalFee === 0) {
                                    set((state) => {
                                        state.formData.step3.showErrors.errorDetails.greaterThan = {
                                            field: 'legalFee',
                                            error: true,
                                        };
                                    });
                                    hasEmptyFields = true;
                                }
                                if (item.value?.length === 0) {
                                    set((state) => {
                                        state.formData.step3.showErrors.errorDetails.greaterThan = {
                                            field: 'amount',
                                            error: true,
                                        };
                                    });
                                    hasEmptyFields = true;
                                }
                                if (item.value?.length > 0 && !item.value[0]) {
                                    set((state) => {
                                        state.formData.step3.showErrors.errorDetails.greaterThan = {
                                            field: 'amount',
                                            error: true,
                                        };
                                    });
                                    hasEmptyFields = true;
                                }
                                break;
                            }
                            case 'between': {
                                const betweenErrors: any = [];
                                if (!item.legalFee || item.legalFee === 0) {
                                    const tempError = {
                                        field: ['legalFee'],
                                        error: true,
                                        index: thresholdIndex - 1,
                                    };
                                    betweenErrors.push(tempError);
                                    hasEmptyFields = true;
                                }
                                if (item.value?.length === 0) {
                                    const tempError = {
                                        field: ['From', 'To'],
                                        error: true,
                                        index: thresholdIndex - 1,
                                    };
                                    betweenErrors.push(tempError);
                                    hasEmptyFields = true;
                                }
                                if (item.value?.length > 0) {
                                    if (!item.value[0] || item.value[0] === 0) {
                                        const tempError = {
                                            field: ['From'],
                                            error: true,
                                            index: thresholdIndex - 1,
                                        };
                                        betweenErrors.push(tempError);
                                        hasEmptyFields = true;
                                    }
                                    if (!item.value[1] || item.value[1] === 0) {
                                        const tempError = {
                                            field: ['To'],
                                            error: true,
                                            index: thresholdIndex - 1,
                                        };
                                        betweenErrors.push(tempError);
                                        hasEmptyFields = true;
                                    }
                                }
                                if (betweenErrors) {
                                    set((state) => {
                                        if (
                                            state.formData.step3.showErrors?.errorDetails?.between
                                                ?.length
                                        ) {
                                            state.formData.step3.showErrors.errorDetails.between = [
                                                ...state.formData.step3.showErrors.errorDetails
                                                    .between,
                                                ...betweenErrors,
                                            ];
                                        } else {
                                            state.formData.step3.showErrors.errorDetails.between = [
                                                ...betweenErrors,
                                            ];
                                        }
                                    });
                                }
                            }
                        }
                    });
                    if (hasEmptyFields || !validateNoThresholdOverlap(payload)) {
                        set((state) => {
                            if (hasEmptyFields) {
                                state.formData.step3.showErrors.missingValue = true;
                            }
                            if (!validateNoThresholdOverlap(payload)) {
                                state.formData.step3.showErrors.overlap = true;
                            }
                        });
                        return;
                    }
                    //validation for empty fee add-on fields
                    const dynamicFeesPayload = get().formData.step3.dynamicLegalFees;
                    const tempErrorDetails: any = [];
                    const questionAnswerTracker = {};
                    let hasMissingValues = false;
                    let hasDuplicateValues = false;
                    dynamicFeesPayload.forEach((field, index) => {
                        if (!field.question || field.question === '' || field.question === null) {
                            hasMissingValues = true;
                            tempErrorDetails.push({
                                field: 'question',
                                error: true,
                                index: index,
                            });
                        }
                        if (
                            !field.description ||
                            field.description === '' ||
                            field.description === null
                        ) {
                            hasMissingValues = true;
                            tempErrorDetails.push({
                                field: 'description',
                                error: true,
                                index: index,
                            });
                        }
                        if (!field.answer || field.answer === '' || field.answer === null) {
                            hasMissingValues = true;
                            tempErrorDetails.push({
                                field: 'answer',
                                error: true,
                                index: index,
                            });
                        }
                        if (!field.amount || field.amount === 0 || field.amount === null) {
                            hasMissingValues = true;
                            tempErrorDetails.push({
                                field: 'amount',
                                error: true,
                                index: index,
                            });
                        }
                        if (
                            !field.description ||
                            field.description === '' ||
                            field.description === null
                        ) {
                            hasMissingValues = true;
                            tempErrorDetails.push({
                                field: 'description',
                                error: true,
                                index: index,
                            });
                        }
                        // Track question-answer pairs to check for duplicates
                        if (field.question && field.answer) {
                            const key = `${field.question}-${field.answer}`;
                            if (questionAnswerTracker[key]) {
                                hasDuplicateValues = true;
                                tempErrorDetails.push({
                                    field: 'question',
                                    error: true,
                                    index: index,
                                });
                                tempErrorDetails.push({
                                    field: 'answer',
                                    error: true,
                                    index: index,
                                });
                            } else {
                                questionAnswerTracker[key] = true;
                            }
                        }
                    });
                    if (tempErrorDetails) {
                        set((state) => {
                            if (state.formData.step3.dynamicFeesErrors?.errorDetails?.length) {
                                state.formData.step3.dynamicFeesErrors.errorDetails = [
                                    ...state.formData.step3.dynamicFeesErrors.errorDetails,
                                    ...tempErrorDetails,
                                ];
                            } else {
                                state.formData.step3.dynamicFeesErrors.errorDetails = [
                                    ...tempErrorDetails,
                                ];
                            }
                        });
                    }
                    if (hasMissingValues) {
                        set((state) => {
                            state.formData.step3.dynamicFeesErrors.missingValue = true;
                        });
                    }
                    if (hasDuplicateValues) {
                        set((state) => {
                            state.formData.step3.dynamicFeesErrors.duplicateValue = true;
                        });
                    }
                    if (
                        get().formData.step3.dynamicFeesErrors.errorDetails.length > 0 ||
                        get().formData.step3.dynamicFeesErrors.dynamicHiddenFieldError ||
                        get().formData.step3.dynamicFeesErrors.optionError
                    ) {
                        return;
                    }
                }
                if (pricingModel === 'Flat Fee') {
                    if (!payload.flatFee || payload.flatFee === '' || payload.flatFee === '0') {
                        // Set showWarning to 'Please enter a flat fee value
                        set((state) => {
                            state.formData.step3.showWarning = 'Please enter a flat fee value';
                        });
                        // Reset showWarning after 3 seconds
                        const timer = window.setTimeout(() => {
                            set((state) => {
                                state.formData.step3.showWarning = null;
                            });
                            // Clear timer
                            window.clearTimeout(timer);
                        }, 3000);
                        // reset-remove unnecessary payload
                        return;
                    }
                    payload.thresholdFee = null;
                }

                // call the API to update the pricing
                // activeQuote is a dependency it needs to have the quoteTemplateId
                if (!isEqual(payload, get().formData.step3.screenOnePayload)) {
                    if (get().activeQuote?.quoteTemplateId) {
                        set((state) => {
                            state.loading = true;
                        });
                        await updateQuoteTemplatePricing(
                            get().activeQuote?.quoteTemplateId,
                            payload,
                        )
                            .then((_response) => {
                                // set isScreenTwo to true to show the screen 2
                                set((state) => {
                                    state.formData.step3.screenOnePayload = payload;
                                    state.formData.step3.isScreenTwo = true;
                                    state.loading = false;
                                });
                            })
                            .catch((error) => {
                                console.error(error);
                                set((state) => {
                                    state.loading = false;
                                    state.formData.step3.showErrors.overlap =
                                        error?.response?.data?.error?.find(
                                            (err: any) => err?.field === 'thresholdFee',
                                        );
                                });
                            });
                    }
                } else {
                    //skipping to next step.
                    set((state) => {
                        state.loading = false;
                        state.formData.step3.isScreenTwo = true;
                    });
                }
            } else if (step === 'step3' && subStep === '2') {
                //reset errors
                set((state) => {
                    state.formData.step3.dynamicFeesErrors = {
                        ...state.formData.step3.dynamicFeesErrors,
                        missingValue: null,
                        duplicateValue: null,
                        errorDetails: [],
                    };
                });

                //validation for empty fee add-on fields
                const dynamicFeesPayload = get().formData.step3.dynamicCostEstimationFields;
                const tempErrorDetails: any = [];
                const questionAnswerTracker = {};
                let hasMissingValues = false;
                let hasDuplicateValues = false;
                dynamicFeesPayload.forEach((field, index) => {
                    if (
                        !field.dynamicPriceCategory ||
                        field.dynamicPriceCategory === '' ||
                        field.dynamicPriceCategory === null
                    ) {
                        hasMissingValues = true;
                        tempErrorDetails.push({
                            field: 'dynamicPriceCategory',
                            error: true,
                            index: index,
                        });
                    }
                    if (!field.question || field.question === '' || field.question === null) {
                        hasMissingValues = true;
                        tempErrorDetails.push({
                            field: 'question',
                            error: true,
                            index: index,
                        });
                    }
                    if (!field.answer || field.answer === '' || field.answer === null) {
                        hasMissingValues = true;
                        tempErrorDetails.push({
                            field: 'answer',
                            error: true,
                            index: index,
                        });
                    }
                    if (
                        !field.description ||
                        field.description === '' ||
                        field.description === null
                    ) {
                        hasMissingValues = true;
                        tempErrorDetails.push({
                            field: 'description',
                            error: true,
                            index: index,
                        });
                    }
                    if (!field.amount || field.amount === 0 || field.amount === null) {
                        hasMissingValues = true;
                        tempErrorDetails.push({
                            field: 'amount',
                            error: true,
                            index: index,
                        });
                    }
                    //track question-answer pairs to check for duplicates
                    if (field.dynamicPriceCategory && field.question && field.answer) {
                        const key = `${field.dynamicPriceCategory}-${field.question}-${field.answer}`;
                        if (questionAnswerTracker[key]) {
                            hasDuplicateValues = true;
                            tempErrorDetails.push({
                                field: 'dynamicPriceCategory',
                                error: true,
                                index: index,
                            });
                            tempErrorDetails.push({
                                field: 'question',
                                error: true,
                                index: index,
                            });
                            tempErrorDetails.push({
                                field: 'answer',
                                error: true,
                                index: index,
                            });
                        } else {
                            questionAnswerTracker[key] = true;
                        }
                    }
                });
                if (tempErrorDetails) {
                    set((state) => {
                        if (state.formData.step3.dynamicFeesErrors?.errorDetails?.length) {
                            state.formData.step3.dynamicFeesErrors.errorDetails = [
                                ...state.formData.step3.dynamicFeesErrors.errorDetails,
                                ...tempErrorDetails,
                            ];
                        } else {
                            state.formData.step3.dynamicFeesErrors.errorDetails = [
                                ...tempErrorDetails,
                            ];
                        }
                    });
                }
                if (hasMissingValues) {
                    set((state) => {
                        state.formData.step3.dynamicFeesErrors.missingValue = true;
                    });
                }
                if (hasDuplicateValues) {
                    set((state) => {
                        state.formData.step3.dynamicFeesErrors.duplicateValue = true;
                    });
                }
                if (
                    get().formData.step3.dynamicFeesErrors.errorDetails.length > 0 ||
                    get().formData.step3.dynamicFeesErrors.costHiddenFieldError ||
                    get().formData.step3.dynamicFeesErrors.optionError
                ) {
                    return;
                }
                //Create the payload for disbursement
                const disbursementPayload: FeeEstimatePayload[] = get()
                    .formData.step3.disbursementValues.filter(
                        (values) => values.id === undefined && values.isAdded === true,
                    )
                    .map((values) => ({
                        description: values.description,
                        amount: values.amount,
                    }));

                //Create the payload for registration fees
                const registrationFeesPayload: FeeEstimatePayload[] = get()
                    .formData.step3.registrationFeeValues.filter(
                        (values) => values.id === undefined && values.isAdded === true,
                    )
                    .map((values) => ({
                        description: values.description,
                        amount: values.amount,
                    }));

                //Call the POST api to add disbursement
                if (disbursementPayload.length > 0) {
                    set((state) => {
                        state.loading = true;
                    });
                    axiosInstance
                        .post<FeeEstimate[]>(
                            `/api/v1/quote-templates/${
                                get().activeQuote.quoteTemplateId
                            }/disbursements-estimates`,
                            disbursementPayload,
                        )
                        .then((disbursementResp) => {
                            set((state) => {
                                state.formData.step3.disbursementValues = [
                                    ...state.formData.step3.disbursementApiValues.map((value) => ({
                                        id: value.feeEstimateId,
                                        description: value.description,
                                        amount: value.amount,
                                        isAdded: true,
                                        isEditMode: false,
                                    })),
                                    ...disbursementResp.data.map((value) => ({
                                        id: value.feeEstimateId,
                                        description: value.description,
                                        amount: value.amount,
                                        isAdded: true,
                                        isEditMode: false,
                                    })),
                                ];
                                state.formData.step3.disbursementApiValues = [
                                    ...state.formData.step3.disbursementApiValues,
                                    ...disbursementResp.data,
                                ];
                                state.formData.step3.isDefaultDisbursementSet = true;
                            });
                            set((state) => {
                                state.loading = false;
                            });
                        })
                        .catch((error) => {
                            console.log(error);
                            set((state) => {
                                state.loading = false;
                            });
                        });
                }

                //API call to add registration fees estimates
                if (registrationFeesPayload.length > 0) {
                    set((state) => {
                        state.loading = true;
                    });
                    axiosInstance
                        .post<FeeEstimate[]>(
                            `/api/v1/quote-templates/${
                                get().activeQuote.quoteTemplateId
                            }/registration-fees-estimates`,
                            registrationFeesPayload,
                        )
                        .then((registrationResp) => {
                            set((state) => {
                                state.formData.step3.registrationFeeValues = [
                                    ...state.formData.step3.registrationFeeApiValues.map(
                                        (value) => ({
                                            id: value.feeEstimateId,
                                            description: value.description,
                                            amount: value.amount,
                                            isAdded: true,
                                            isEditMode: false,
                                        }),
                                    ),
                                    ...registrationResp.data.map((value) => ({
                                        id: value.feeEstimateId,
                                        description: value.description,
                                        amount: value.amount,
                                        isAdded: true,
                                        isEditMode: false,
                                    })),
                                ];
                                state.formData.step3.registrationFeeApiValues = [
                                    ...state.formData.step3.registrationFeeApiValues,
                                    ...registrationResp.data,
                                ];
                                state.formData.step3.isDefaultRegistrationSet = true;
                            });
                            set((state) => {
                                state.loading = false;
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                            set((state) => {
                                state.loading = false;
                            });
                        });
                }

                //Call API to add title insurance
                if (
                    get().formData.step0.transactionType === 'Purchase' ||
                    get().formData.step0.transactionType === 'Refinance'
                ) {
                    const titleInsurancePayload: FeeEstimatePayload[] = get()
                        .formData.step3.titleInsuranceEstimation.filter(
                            (value) => value.id === undefined && value.isAdded === true,
                        )
                        .map((value) => ({
                            description: value.description,
                            amount: value.amount,
                        }));

                    if (titleInsurancePayload.length > 0) {
                        set((state) => {
                            state.loading = true;
                        });
                        try {
                            const titleInsuranceResp = await addNewTitleInsuranceEstimates(
                                get().activeQuote.quoteTemplateId,
                                titleInsurancePayload,
                            );
                            set((state) => {
                                state.formData.step3.titleInsuranceEstimation = [
                                    ...state.formData.step3.titleInsuranceApiValues.map(
                                        (value) => ({
                                            id: value.feeEstimateId,
                                            description: value.description,
                                            amount: value.amount,
                                            isAdded: true,
                                            isEditMode: false,
                                        }),
                                    ),
                                    ...titleInsuranceResp.data.map((value) => ({
                                        id: value.feeEstimateId,
                                        description: value.description,
                                        amount: value.amount,
                                        isAdded: true,
                                        isEditMode: false,
                                    })),
                                ];
                                state.formData.step3.titleInsuranceApiValues = [
                                    ...state.formData.step3.titleInsuranceApiValues,
                                    ...titleInsuranceResp.data,
                                ];
                                state.formData.step3.isDefaultTitleInsuranceSet = true;
                                state.loading = false;
                            });
                        } catch (error: any) {
                            console.error(error);
                            set((state) => {
                                state.loading = false;
                            });
                        }
                    }
                }

                set((state) => {
                    state.currentStep = state.currentStep + 1;
                });
            }

            if (step === 'step4' && subStep === '1') {
                const payload = {
                    createFormManually: get()?.formData?.step0?.intakeFormAdded === 'Manually',
                    createByReference: get()?.formData?.step0?.intakeFormAdded === 'Reference',
                    copyFromExisting: get()?.formData?.step0?.intakeFormAdded === 'Copy',
                    referencedQuoteTemplateId:
                        get()?.formData?.step0?.referencedIntakeOnlyQuoteTemplateId,
                };

                if (!get()?.formData.step0.isIntakeCreated) {
                    try {
                        if (get()?.activeQuote?.quoteTemplateId) {
                            set((state) => {
                                state.loading = true;
                            });
                            const intakeTemplate = await createIntakeByReference(
                                get()?.activeQuote?.quoteTemplateId,
                                payload,
                            );
                            set((state) => {
                                state.formData.step0 = {
                                    ...state.formData.step0,
                                    isIntakeCreated: true,
                                };
                            });
                            if (intakeTemplate.data) {
                                const currentData = { ...get().formData.step4 };
                                currentData.apiResponse = intakeTemplate?.data;
                                currentData.intakeFormDescription =
                                    intakeTemplate.data.headerDescription ??
                                    get().formData.step4.intakeFormDescription;
                                currentData.textAfterSubmitIntakeForClient =
                                    intakeTemplate.data.footerTemplate ??
                                    get().formData.step4.textAfterSubmitIntakeForClient;
                                currentData.quoteTemplateId = intakeTemplate.data.quoteTemplateId;
                                currentData.templateFormId = intakeTemplate.data.templateFormId;
                                currentData.templateFormFields =
                                    intakeTemplate.data.templateFormFields;
                                set((state) => {
                                    state.formData.step4 = currentData;
                                });
                            }
                            set((state) => {
                                state.loading = false;
                            });
                        }
                    } catch (error: any) {
                        set((state) => {
                            state.error = error.response?.data || 'Something went wrong!';
                            state.loading = false;
                        });
                        const timeout = window.setTimeout(() => {
                            set((state) => {
                                state.error = null;
                            });
                            window.clearTimeout(timeout);
                        }, 5000);
                    }
                }
            } else if (step === 'step4') {
                //validate description length
                if (get().formData.step4.intakeFormDescription?.length > 4000) {
                    return;
                }

                // validate footerTemplate length
                if (get().formData.step4.textAfterSubmitIntakeForClient?.length > 10000) {
                    return;
                }

                const apiResponse = get().formData.step4.apiResponse;
                const activeQuote = get().activeQuote;

                const payload = get().preparePayloadForStep4();

                if (
                    get()?.formData.step0.intakeFormAdded === 'Manually' ||
                    get()?.formData.step0.intakeFormAdded === 'Copy'
                ) {
                    if (apiResponse) {
                        // Will be a case for PUT call or no call
                        try {
                            if (activeQuote?.quoteTemplateId) {
                                set((state) => {
                                    state.loading = true;
                                });
                                //prepare payload for update
                                const {
                                    templateFormId: _templateFormId,
                                    quoteTemplateId: _quoteTemplateId,
                                    ...updatePayload
                                } = payload;
                                const UpdateTemplateFormResponse = await updateTemplateForm(
                                    activeQuote?.quoteTemplateId,
                                    payload.templateFormId,
                                    updatePayload,
                                );
                                set((state) => {
                                    state.loading = false;
                                });
                                if (UpdateTemplateFormResponse.data) {
                                    set((state) => {
                                        state.formData.step4.apiResponse =
                                            UpdateTemplateFormResponse.data;
                                        state.formData.step4.intakeFormDescription =
                                            UpdateTemplateFormResponse.data.headerDescription ?? '';
                                        state.formData.step4.textAfterSubmitIntakeForClient =
                                            UpdateTemplateFormResponse.data.footerTemplate ?? '';
                                        state.formData.step4.quoteTemplateId =
                                            UpdateTemplateFormResponse.data.quoteTemplateId;
                                        state.formData.step4.templateFormId =
                                            UpdateTemplateFormResponse.data.templateFormId;
                                        state.formData.step4.templateFormFields =
                                            UpdateTemplateFormResponse.data.templateFormFields;
                                        state.currentStep = state.currentStep + 1;
                                    });
                                }
                            }
                        } catch (error: any) {
                            set((state) => {
                                state.error = error.response?.data || 'Something went wrong!';
                                state.loading = false;
                            });
                            const timeout = window.setTimeout(() => {
                                set((state) => {
                                    state.error = null;
                                });
                                window.clearTimeout(timeout);
                            }, 5000);
                        }
                    } else {
                        // Will be a case for POST call
                        try {
                            if (activeQuote?.quoteTemplateId) {
                                set((state) => {
                                    state.loading = true;
                                });
                                const {
                                    templateFormId: _templateFormId,
                                    quoteTemplateId: _quoteTemplateId,
                                    ...createPayload
                                } = payload;
                                const createTemplateFormResponse = await createTemplateForm(
                                    activeQuote.quoteTemplateId,
                                    createPayload,
                                );
                                set((state) => {
                                    state.loading = false;
                                });

                                // set the data to apiResponse and move the user to next step
                                if (createTemplateFormResponse) {
                                    set((state) => {
                                        state.formData.step4.apiResponse =
                                            createTemplateFormResponse.data;
                                        state.formData.step4.intakeFormDescription =
                                            createTemplateFormResponse.data.headerDescription ??
                                            get().formData.step4.intakeFormDescription;
                                        state.formData.step4.textAfterSubmitIntakeForClient =
                                            createTemplateFormResponse.data.footerTemplate ??
                                            get().formData.step4.textAfterSubmitIntakeForClient;
                                        state.formData.step4.quoteTemplateId =
                                            createTemplateFormResponse.data.quoteTemplateId;
                                        state.formData.step4.templateFormId =
                                            createTemplateFormResponse.data.templateFormId;
                                        state.formData.step4.templateFormFields =
                                            createTemplateFormResponse.data.templateFormFields;
                                        state.currentStep = state.currentStep + 1;
                                    });
                                }
                            }
                        } catch (error: any) {
                            set((state) => {
                                state.error = error.response?.data || 'Something went wrong!';
                                state.loading = false;
                            });

                            const timeout = window.setTimeout(() => {
                                set((state) => {
                                    state.error = null;
                                });

                                window.clearTimeout(timeout);
                            }, 5000);
                        }
                    }
                } else {
                    set((state) => {
                        state.currentStep = state.currentStep + 1;
                    });
                }
            }

            if (step === 'step5' && subStep === '1') {
                const payload: EmailTemplatePayloadInterface = {
                    emailTemplateType: 'IntakeEmailTemplate',
                    isQuoteApprovalEnable: get().formData.step5.isQuoteApprovalOn,
                    isQuoteReminderOn: false,
                    emailSubject: get().formData.step5.subject,
                    emailBodyIntroduction: get().formData.step5.bodyBeforeLink,
                    emailBodyClosing: get().formData.step5.bodyAfterLink,
                    emailCC:
                        get().formData.step5.contactCC?.length > 0
                            ? get().formData.step5.contactCC
                            : null,
                    emailReminder: null,
                    emailSignature: get().formData.step5.signatureOfEmail,
                    expiry: get().formData.step5.expiry,
                };

                if (get().formData.step5.intakeEmailTemplateId) {
                    await updateIntakeEmailTemplate(
                        payload,
                        get().activeQuote.quoteTemplateId,
                        get().formData,
                        get().errorData,
                        (data: any) => {
                            set((state) => {
                                state.formData.step5 = {
                                    ...state.formData.step5,
                                    ...data,
                                };
                            });
                        },
                        (error: string | null) =>
                            set((state) => {
                                state.error = error;
                            }),
                        (errorData: any) =>
                            set((state) => {
                                state.errorData = errorData;
                            }),
                        (isLoading: boolean) =>
                            set((state) => {
                                state.loading = isLoading;
                            }),
                    );
                } else {
                    await addIntakeEmailTemplate(
                        payload,
                        get().activeQuote.quoteTemplateId,
                        get().formData,
                        get().errorData,
                        (data: any) => {
                            set((state) => {
                                state.formData.step5 = {
                                    ...state.formData.step5,
                                    ...data,
                                };
                            });
                        },
                        (error: string | null) =>
                            set((state) => {
                                state.error = error;
                            }),
                        (errorData: any) =>
                            set((state) => {
                                state.errorData = errorData;
                            }),
                        (isLoading: boolean) =>
                            set((state) => {
                                state.loading = isLoading;
                            }),
                    );
                }
                const postTemplatePayload: PostIntakeEmailTemplatePayloadInterface = {
                    isPostIntakeEnable: get().formData.step0.isPostIntakeEnable,
                    emailSubject: get().formData.step5.postIntakeSubject,
                    intakeCompletionText: get().formData.step4.textAfterSubmitIntakeForClient,
                };
                await updatePostIntakeEmailTemplate(
                    postTemplatePayload,
                    get().activeQuote.quoteTemplateId,
                    get().formData,
                    get().errorData,
                    (data: any) => {
                        set((state) => {
                            state.formData.step5 = {
                                ...state.formData.step5,
                                ...data,
                            };
                        });
                    },
                    (error: string | null) =>
                        set((state) => {
                            state.error = error;
                        }),
                    (errorData: any) =>
                        set((state) => {
                            state.errorData = errorData;
                        }),
                    (isLoading: boolean) =>
                        set((state) => {
                            state.loading = isLoading;
                        }),
                );
            } else if (step === 'step5' && subStep === '2') {
                const payload: EmailTemplatePayloadInterface = {
                    emailTemplateType: 'IntakeEmailReminderTemplate',
                    isQuoteApprovalEnable: false,
                    isQuoteReminderOn: get().formData.step5.isQuoteReminderOn,
                    emailSubject: get().formData.step5.reminderSubject,
                    emailBodyIntroduction: get().formData.step5.reminderBodyBeforeLink,
                    emailBodyClosing: get().formData.step5.reminderBodyAfterLink,
                    emailCC:
                        get().formData.step5.reminderContactsCC?.length > 0
                            ? get().formData.step5.reminderContactsCC
                            : null,
                    emailReminder: {
                        emailFrequencyOfReminder: get().formData.step5.emailFrequencyOfReminder,
                        reminderTime: get().formData.step5.reminderTime,
                        totalNumberOfReminders: get().formData.step5.totalNumberOfReminders,
                    },
                    emailSignature: get().formData.step5.reminderSignatureOfEmail,
                    enableReminderExhaustNotification:
                        get().formData.step5.enableReminderExhaustNotification || false,
                };

                if (get().formData.step5.intakeReminderEmailTemplateId) {
                    await updateIntakeEmailReminderTemplate(
                        payload,
                        get().activeQuote.quoteTemplateId,
                        get().formData,
                        get().errorData,
                        (data: any) => {
                            set((state) => {
                                state.formData.step5 = {
                                    ...state.formData.step5,
                                    ...data,
                                };
                            });
                        },
                        (error: string | null) =>
                            set((state) => {
                                state.error = error;
                            }),
                        (errorData: any) =>
                            set((state) => {
                                state.errorData = errorData;
                            }),
                        (isLoading: boolean) =>
                            set((state) => {
                                state.loading = isLoading;
                            }),
                    );
                } else {
                    await addIntakeEmailReminderTemplate(
                        payload,
                        get().activeQuote.quoteTemplateId,
                        get().formData,
                        get().errorData,
                        (data: any) => {
                            set((state) => {
                                state.formData.step5 = {
                                    ...state.formData.step5,
                                    ...data,
                                };
                            });
                        },
                        (error: string | null) =>
                            set((state) => {
                                state.error = error;
                            }),
                        (errorData: any) =>
                            set((state) => {
                                state.errorData = errorData;
                            }),
                        (isLoading: boolean) =>
                            set((state) => {
                                state.loading = isLoading;
                            }),
                    );
                }
                const { activeQuote, formData } = get();
                const statusPromises: Promise<any>[] = [];

                const updateTemplateStatus = (
                    templateId: number | string,
                    formId: string | any,
                ) => {
                    if (templateId && formId) {
                        statusPromises.push(
                            updateQuoteIntakeTemplateStatus(templateId, formId, 'Completed'),
                        );
                    }
                };

                updateTemplateStatus(activeQuote?.quoteTemplateId, formData.step1.templateFormId);
                updateTemplateStatus(activeQuote?.quoteTemplateId, formData.step4.templateFormId);

                try {
                    await Promise.all(statusPromises);
                } catch (err: any) {
                    console.error(err);
                }
            }
        },
        fetchQuoteTemplateDataOnEveryStep: async (
            step,
            quoteIntakeId,
            substep,
            isCalledFromStep1 = false,
        ) => {
            if (quoteIntakeId) {
                if (step === 'step0') {
                    // Fetch in parallel but apply in order, so a missing intake form still throws
                    // only after the quote template has been applied
                    const [quoteTemplateResult, intakeTemplateResult] = await Promise.allSettled([
                        fetchQuoteTemplateById(quoteIntakeId),
                        getIntakeTemplateForms(quoteIntakeId),
                    ]);
                    if (quoteTemplateResult.status === 'rejected') {
                        throw quoteTemplateResult.reason;
                    }
                    const quoteTemplateResp = quoteTemplateResult.value;
                    set((state) => {
                        state.activeQuote = quoteTemplateResp.data;
                        state.formData.step0.templateName =
                            quoteTemplateResp.data.quoteTemplateName;
                        state.formData.step0.transactionType = quoteTemplateResp.data.process;
                        state.formData.step0.assignedLawyer =
                            quoteTemplateResp.data.quoteApproverId;
                        state.formData.step0.referencedIntakeOnlyQuoteTemplateId =
                            quoteTemplateResp.data.referencedIntakeOnlyQuoteTemplateId;
                        state.formData.step0.referencedIntakeOnlyQuoteTemplateName =
                            quoteTemplateResp.data.referencedIntakeOnlyQuoteTemplateName;
                        state.formData.step0.isPostIntakeEnable =
                            quoteTemplateResp.data.isPostIntakeEnable;
                        state.formData.step0.province = quoteTemplateResp.data.province;
                        state.formData.step0.intakeFormAdded =
                            quoteTemplateResp.data.intakeFormAdded;
                        state.formData.step0.transactionTypePrevPayload = {
                            process: quoteTemplateResp.data.process,
                            quoteApproverId: quoteTemplateResp.data.quoteApproverId,
                            quoteTemplateName: quoteTemplateResp.data.quoteTemplateName,
                            province: quoteTemplateResp.data.province,
                        };
                        // state.formData.step2.reminderSignatureOfEmail =
                        //   quoteReminderTemplateResp.data.emailSignature || ''
                        state.formData.step3.isTransactionInToronto = quoteTemplateResp.data
                            .isConductTransactionInToronto
                            ? 'Yes'
                            : 'No';
                        state.formData.step3.flatFeeValue =
                            quoteTemplateResp.data.flatFee?.toString();
                        state.formData.step3.legalFeeDiscount =
                            quoteTemplateResp.data.legalFeeDiscount?.toString();
                        state.formData.step3.pricingModel =
                            quoteTemplateResp.data?.thresholdFee?.length &&
                            quoteTemplateResp.data?.thresholdFee?.length > 0
                                ? 'Variable pricing'
                                : 'Flat Fee';
                        state.formData.step3.lessThanValue =
                            quoteTemplateResp.data.thresholdFee?.find(
                                (value) => value.operatorType === 'lessThan',
                            ) || {
                                operatorType: 'lessThan',
                                value: [500000],
                                legalFee: 800,
                            };
                        state.formData.step3.greaterThanValue =
                            quoteTemplateResp.data.thresholdFee?.find(
                                (value) => value.operatorType === 'greaterThan',
                            ) || {
                                operatorType: 'greaterThan',
                                value: [1500000],
                                legalFee: 1200,
                            };

                        state.formData.step3.betweenValue =
                            quoteTemplateResp.data.thresholdFee?.filter(
                                (value) => value.operatorType === 'between',
                            )?.length
                                ? quoteTemplateResp.data.thresholdFee?.filter(
                                      (value) => value.operatorType === 'between',
                                  )
                                : [
                                      {
                                          operatorType: 'between',
                                          value: [500000, 1500000],
                                          legalFee: 1000,
                                      },
                                  ];

                        state.formData.step3.screenOnePayload = {
                            isConductTransactionInToronto:
                                quoteTemplateResp.data?.isConductTransactionInToronto,
                            flatFee: quoteTemplateResp.data?.flatFee?.toString(),
                            thresholdFee:
                                quoteTemplateResp.data?.thresholdFee?.length &&
                                quoteTemplateResp.data?.thresholdFee?.length > 0
                                    ? quoteTemplateResp.data?.thresholdFee
                                    : null,
                            pricingModel: quoteTemplateResp.data?.pricingModel,
                        };
                    });
                    if (intakeTemplateResult.status === 'rejected') {
                        throw intakeTemplateResult.reason;
                    }
                    const IntakeTemplate = intakeTemplateResult.value;
                    if (IntakeTemplate?.data?.templateFormType === 'IntakeForm') {
                        set((state) => {
                            state.formData.step0 = {
                                ...state.formData.step0,
                                isIntakeCreated: true,
                                intakeFormStatus: IntakeTemplate?.data?.status,
                            };
                        });
                    }
                }

                // Quote design fetch api call
                if (step === 'step1') {
                    try {
                        const quoteTemplateResp = await getQuoteTemplateForms(quoteIntakeId);
                        set((state) => {
                            state.formData.step1.apiResponse = quoteTemplateResp.data;
                            state.formData.step1.headerDescription =
                                quoteTemplateResp.data.headerDescription ?? '';
                            state.formData.step1.footerTemplate =
                                quoteTemplateResp.data.footerTemplate ?? '';
                            state.formData.step1.templateFormId =
                                quoteTemplateResp.data.templateFormId || null;
                            state.formData.step1.quoteTemplateId =
                                quoteTemplateResp.data.quoteTemplateId ?? null;
                            state.formData.step1.templateFormFields =
                                quoteTemplateResp.data.templateFormFields;
                        });
                    } catch (error: any) {
                        const templateType = get().formData.step0.transactionType;
                        get().setDefaultTemplateFormFields(templateType);
                        throw error;
                    }
                }

                //Pricing Details fetch (Only for Dynamic fields)
                if (step === 'step3' && substep === '1') {
                    const dynamicFieldsResp = await getDynamicPrices(quoteIntakeId, ['LegalFee']);
                    set((state) => {
                        state.formData.step3.dynamicLegalFees = dynamicFieldsResp.data;
                    });
                }

                //cost estimation fetch
                if (step === 'step3' && substep === '2') {
                    const transactionType = get().formData.step0.transactionType;
                    const hasTitleInsurance =
                        transactionType === 'Purchase' ||
                        transactionType === 'Refinance' ||
                        transactionType === 'PrivateLender';

                    // Fetch in parallel but apply in the original order
                    const titleInsurancePromise = hasTitleInsurance
                        ? getTitleInsuranceEstimates(quoteIntakeId)
                        : null;
                    // Handled below once the other estimates are applied
                    titleInsurancePromise?.catch(() => undefined);
                    const [disbursementResp, registrationFeesResp, dynamicFieldsResp] =
                        await Promise.all([
                            axiosInstance.get<FeeEstimate[]>(
                                `/api/v1/quote-templates/${quoteIntakeId}/disbursements-estimates`,
                            ),
                            axiosInstance.get<FeeEstimate[]>(
                                `/api/v1/quote-templates/${quoteIntakeId}/registration-fees-estimates`,
                            ),
                            getDynamicPrices(quoteIntakeId, ['DisbursementFee', 'RegistrationFee']),
                        ]);

                    set((state) => {
                        state.formData.step3.disbursementApiValues = disbursementResp.data;
                        state.formData.step3.disbursementValues = disbursementResp.data.map(
                            (value) => ({
                                id: value.feeEstimateId,
                                description: value.description,
                                amount: value.amount,
                                isAdded: true,
                                isEditMode: false,
                            }),
                        );
                        state.formData.step3.registrationFeeApiValues = registrationFeesResp.data;
                        state.formData.step3.registrationFeeValues = registrationFeesResp.data.map(
                            (value) => ({
                                id: value.feeEstimateId,
                                description: value.description,
                                amount: value.amount,
                                isAdded: true,
                                isEditMode: false,
                            }),
                        );
                        state.formData.step3.isDefaultDisbursementSet = true;
                        state.formData.step3.isDefaultRegistrationSet = true;
                        state.formData.step3.dynamicCostEstimationFields = dynamicFieldsResp.data;
                    });

                    //Call api for title Insurance
                    if (titleInsurancePromise) {
                        const titleInsuranceResp = await titleInsurancePromise;

                        set((state) => {
                            state.formData.step3.titleInsuranceApiValues = titleInsuranceResp.data;
                            state.formData.step3.titleInsuranceEstimation =
                                titleInsuranceResp.data.map((value) => ({
                                    id: value.feeEstimateId,
                                    description: value.description,
                                    amount: value.amount,
                                    isAdded: true,
                                    isEditMode: false,
                                }));
                            state.formData.step3.isDefaultTitleInsuranceSet = true;
                        });
                    }
                }

                //Quote emails
                if (step === 'step2' && substep === '1') {
                    try {
                        const quoteTemplateResp = await axiosInstance.get<EmailTemplate>(
                            `/api/v1/quote-templates/${quoteIntakeId}/quote-email-template`,
                        );
                        const {
                            emailTemplateId,
                            isQuoteApprovalEnable,
                            emailSubject,
                            emailBodyIntroduction,
                            emailBodyClosing,
                            // emailSignature,
                            emailCC,
                            expiry,
                            disclaimerText,
                        } = quoteTemplateResp.data;
                        set((state) => {
                            state.formData.step2 = {
                                ...state.formData.step2,
                                emailTemplateId: emailTemplateId,
                                isScreenTwo: false,
                                subject: emailSubject || '',
                                bodyBeforePricing: emailBodyIntroduction || '',
                                bodyAfterPricing: emailBodyClosing || '',
                                // signatureOfEmail: emailSignature || '',
                                contactsCC: emailCC || [],
                                isQuoteApprovalOn: isQuoteApprovalEnable,
                                expiry: expiry,
                                disclaimerText: disclaimerText,
                                quoteEmailTemplatePrevPayload: isCalledFromStep1
                                    ? {}
                                    : {
                                          emailTemplateType: 'QuoteEmailTemplate',
                                          isQuoteApprovalEnable: true,
                                          isQuoteReminderOn: false,
                                          emailSubject: emailSubject,
                                          emailBodyIntroduction: emailBodyIntroduction,
                                          emailBodyClosing: emailBodyClosing,
                                          // emailSignature: emailSignature,
                                          emailCC: emailCC,
                                          emailReminder: null,
                                      },
                            };
                        });
                    } catch (error) {
                        set((state) => {
                            const templateType = state.formData.step0.transactionType;
                            state.formData.step2.bodyBeforePricing =
                                defaultBodyBeforePricing[templateType];
                        });
                        throw error;
                    }
                }

                // Quote reminder email template fetch
                if (step === 'step2' && substep === '2') {
                    try {
                        const quoteReminderTemplateResp = await axiosInstance.get<EmailTemplate>(
                            `/api/v1/quote-templates/${quoteIntakeId}/quote-email-reminder-template`,
                        );
                        const {
                            emailTemplateId,
                            isQuoteReminderOn,
                            isQuoteApprovalEnable,
                            emailSubject,
                            emailBodyIntroduction,
                            emailBodyClosing,
                            // emailSignature,
                            totalNumberOfReminders,
                            frequencyOfReminder,
                            reminderTime,
                            emailCC,
                            enableReminderExhaustNotification,
                            disclaimerText,
                        } = quoteReminderTemplateResp.data;
                        set((state) => {
                            state.formData.step2 = {
                                ...state.formData.step2,
                                emailReminderTemplateId: emailTemplateId,
                                reminderSubject: emailSubject || '',
                                reminderBodyBeforePricing: emailBodyIntroduction || '',
                                reminderBodyAfterPricing: emailBodyClosing || '',
                                // reminderSignatureOfEmail: emailSignature || '',
                                reminderContactsCC: emailCC || [],
                                totalNumberOfReminders: totalNumberOfReminders || 2,
                                emailFrequencyOfReminder: frequencyOfReminder || 'EverySecondDay',
                                reminderTime: reminderTime
                                    ? formatTimeSlots(reminderTime)
                                    : 'Morning(09:00 AM)',
                                isQuoteReminderOn: isQuoteReminderOn,
                                quouteEmailReminderPrevPayload: isCalledFromStep1
                                    ? {}
                                    : {
                                          emailTemplateType: 'QuoteEmailReminderTemplate',
                                          isQuoteApprovalEnable: isQuoteApprovalEnable,
                                          isQuoteReminderOn: isQuoteReminderOn,
                                          emailSubject: emailSubject,
                                          emailBodyIntroduction: emailBodyIntroduction,
                                          emailBodyClosing: emailBodyClosing,
                                          // emailSignature: emailSignature,
                                          emailCC: emailCC && emailCC?.length > 0 ? emailCC : null,
                                          emailReminder: {
                                              totalNumberOfReminders: totalNumberOfReminders,
                                              emailFrequencyOfReminder: frequencyOfReminder,
                                              reminderTime: reminderTime
                                                  ? formatTimeSlots(reminderTime)
                                                  : '',
                                          },
                                      },
                                enableReminderExhaustNotification:
                                    enableReminderExhaustNotification,
                                reminderDisclaimerText: disclaimerText,
                            };
                        });
                    } catch (error) {
                        set((state) => {
                            const templateType = state.formData.step0.transactionType;
                            state.formData.step2.reminderBodyBeforePricing =
                                defaultBodyBeforePricingReminder[templateType];
                        });
                        throw error;
                    }
                }

                //Intake design fetch
                if (
                    (step === 'step4' && substep === '2') ||
                    (step === 'step4' && substep === '1')
                ) {
                    set((state) => {
                        state.loading = true;
                    });
                    try {
                        const IntakeTemplate = await getIntakeTemplateForms(quoteIntakeId);
                        if (IntakeTemplate?.data) {
                            // Format and Modify the FE state as per the API response
                            const currentStep4State = { ...get().formData.step4 };

                            currentStep4State.apiResponse = IntakeTemplate.data;
                            currentStep4State.intakeFormDescription =
                                IntakeTemplate.data.headerDescription ??
                                get().formData.step4.intakeFormDescription;
                            currentStep4State.textAfterSubmitIntakeForClient =
                                IntakeTemplate.data.footerTemplate ??
                                get()?.formData?.step4?.textAfterSubmitIntakeForClient;
                            currentStep4State.quoteTemplateId = IntakeTemplate.data.quoteTemplateId;
                            currentStep4State.templateFormId = IntakeTemplate.data.templateFormId;
                            currentStep4State.templateFormFields =
                                IntakeTemplate.data.templateFormFields ?? [];
                            // Set the Template Data on step4 apiResponse
                            set((state) => {
                                state.formData.step4 = currentStep4State;
                            });
                        }
                        set((state) => {
                            state.loading = false;
                        });
                    } catch (error) {
                        set((state) => {
                            state.loading = false;
                        });
                        const templateType = get().formData.step0.transactionType;
                        get().setDefaultTemplateFormFields(templateType);
                        throw error;
                    } finally {
                        set((state) => {
                            state.loading = false;
                        });
                    }
                }

                //Intake email template
                if (step === 'step5' && substep === '1') {
                    // Fetch in parallel but apply in the original order
                    const [postIntakeResult, intakeEmailTemplateResult] = await Promise.allSettled([
                        getPostIntakeTemplateInfo(quoteIntakeId),
                        axiosInstance.get<EmailTemplate>(
                            `/api/v1/quote-templates/${quoteIntakeId}/intake-email-template`,
                        ),
                    ]);

                    //Fetch the data of the post intake email template
                    if (postIntakeResult.status === 'fulfilled') {
                        const { emailSubject } = postIntakeResult.value.data;

                        set((state) => {
                            state.formData.step5 = {
                                ...state.formData.step5,
                                postIntakeSubject: emailSubject,
                            };
                        });
                    } else {
                        console.error(
                            'Failed to fetch post intake template info',
                            postIntakeResult.reason,
                        );
                    }

                    if (intakeEmailTemplateResult.status === 'rejected') {
                        throw intakeEmailTemplateResult.reason;
                    }
                    const intakeEmailTemplateResp = intakeEmailTemplateResult.value;
                    // destructuring data coming from API
                    const {
                        emailTemplateId,
                        isQuoteApprovalEnable,
                        emailBodyClosing,
                        emailBodyIntroduction,
                        emailCC,
                        // emailSignature,
                        emailSubject,
                        expiry,
                    } = intakeEmailTemplateResp.data;
                    //Mapping and setting into state
                    set((state) => {
                        state.formData.step5 = {
                            ...state.formData.step5,
                            intakeEmailTemplateId: emailTemplateId,
                            subject: emailSubject || '',
                            bodyBeforeLink: emailBodyIntroduction || '',
                            bodyAfterLink: emailBodyClosing || '',
                            // signatureOfEmail: emailSignature || '',
                            isQuoteApprovalOn: isQuoteApprovalEnable,
                            contactCC: emailCC || [],
                            expiry: expiry,
                            intakeEmailStepOnePrevPayload: isCalledFromStep1
                                ? {}
                                : {
                                      emailTemplateType: 'IntakeEmailTemplate',
                                      isQuoteApprovalEnable: isQuoteApprovalEnable,
                                      isQuoteReminderOn: false,
                                      emailSubject: emailSubject,
                                      emailBodyIntroduction: emailBodyIntroduction,
                                      emailBodyClosing: emailBodyClosing,
                                      emailCC: emailCC && emailCC?.length > 0 ? emailCC : null,
                                      emailReminder: null,
                                      // emailSignature: emailSignature,
                                  },
                        };
                    });
                }

                //Intake reminder email template
                if (step === 'step5' && substep === '2') {
                    const intakeEmailReminderResp = await axiosInstance.get<EmailTemplate>(
                        `/api/v1/quote-templates/${quoteIntakeId}/intake-email-reminder-template`,
                    );
                    // destructuring data coming from API
                    const {
                        emailTemplateId,
                        isQuoteReminderOn,
                        emailBodyClosing,
                        emailBodyIntroduction,
                        emailCC,
                        // emailSignature,
                        emailSubject,
                        frequencyOfReminder,
                        reminderTime,
                        totalNumberOfReminders,
                        enableReminderExhaustNotification,
                    } = intakeEmailReminderResp.data;
                    set((state) => {
                        state.formData.step5 = {
                            ...state.formData.step5,
                            intakeReminderEmailTemplateId: emailTemplateId,
                            reminderSubject: emailSubject || '',
                            reminderBodyBeforeLink: emailBodyIntroduction || '',
                            reminderBodyAfterLink: emailBodyClosing || '',
                            // reminderSignatureOfEmail: emailSignature || '',
                            reminderContactsCC: emailCC || [],
                            isQuoteReminderOn: isQuoteReminderOn,
                            emailFrequencyOfReminder: frequencyOfReminder || 'EveryThirdDay',
                            reminderTime: reminderTime
                                ? formatTimeSlots(reminderTime)
                                : 'Morning(09:00 AM)',
                            totalNumberOfReminders: totalNumberOfReminders || 3,
                            intakeEmailTemplatePrevPayload: isCalledFromStep1
                                ? {}
                                : {
                                      emailTemplateType: 'IntakeEmailReminderTemplate',
                                      isQuoteApprovalEnable: false,
                                      isQuoteReminderOn: isQuoteReminderOn,
                                      emailSubject: emailSubject,
                                      emailBodyIntroduction: emailBodyIntroduction,
                                      emailBodyClosing: emailBodyClosing,
                                      emailCC: emailCC && emailCC?.length > 0 ? emailCC : null,
                                      emailReminder: {
                                          emailFrequencyOfReminder: frequencyOfReminder,
                                          reminderTime: reminderTime
                                              ? formatTimeSlots(reminderTime)
                                              : '',
                                          totalNumberOfReminders: totalNumberOfReminders,
                                      },
                                      // emailSignature: emailSignature,
                                  },
                            enableReminderExhaustNotification: enableReminderExhaustNotification,
                        };
                    });
                }
            }
        },
        resetSetupState: () => {
            set(initialState);
        },
        // Todo: remove resetState method once the new resetSetupState method is thoroughly tested
        resetState: () =>
            set((state) => {
                state.activeQuote = {} as ActiveQuote;
                state.currentStep = 0;
                state.loading = false;
                state.error = null;
                state.errorData = {
                    step0: {
                        transactionType: '',
                        templateName: '',
                        assignedLawyer: 0,
                    },
                    step2: {
                        quoteEmailTemplateError: {
                            emailSubject: undefined,
                            emailSignature: undefined,
                            emailBodyClosing: undefined,
                            emailBodyIntroduction: undefined,
                        },
                        quouteRemindertemplateError: {
                            emailSubject: undefined,
                            emailSignature: undefined,
                            emailBodyClosing: undefined,
                            emailBodyIntroduction: undefined,
                        },
                    },
                    step5: {
                        intakeEmailTemplateError: {
                            emailSubject: undefined,
                            emailSignature: undefined,
                            emailBodyClosing: undefined,
                            emailBodyIntroduction: undefined,
                        },
                        intakeReminderTemplateErrors: {
                            emailSubject: undefined,
                            emailSignature: undefined,
                            emailBodyClosing: undefined,
                            emailBodyIntroduction: undefined,
                        },
                    },
                };
                state.availableCcContacts = [];
                state.formData = {
                    ...state.formData,
                    step0: {
                        showError: undefined,
                        transactionType: '',
                        templateName: '',
                        assignedLawyer: 0,
                        transactionTypePrevPayload: {},
                    },
                    step1: {
                        apiResponse: null,
                        headerDescription: '',
                        footerTemplate: '',
                        quoteTemplateId: null,
                        templateFormId: null,
                        templateFormType: 'QuoteForm',
                        templateFormFields: [],
                    },
                    step2: {
                        emailTemplateId: null,
                        emailTemplateType: '',
                        emailReminderTemplateId: null,
                        isScreenTwo: false,
                        subject: 'Quote email',
                        bodyBeforePricing: getDefaultBodyBeforePricing(
                            state.formData.step0.transactionType,
                            'email',
                        ),
                        bodyAfterPricing:
                            'Above are the total costs associated with your transaction. Let me know if you have any questions. if you would like to proceed, please click the "Accept" button and you will receive an email link to our intake form.',
                        signatureOfEmail: '',
                        contactsCC: [],
                        reminderSubject: 'Quote email reminder',
                        reminderBodyBeforePricing: getDefaultBodyBeforePricing(
                            state.formData.step0.transactionType,
                            'reminder',
                        ),
                        reminderBodyAfterPricing:
                            'Above are the total costs associated with your transaction. Let me know if you have any questions. if you would like to proceed, please click the "Accept" button and you will receive an email link to our intake form.',
                        reminderSignatureOfEmail: '',
                        reminderContactsCC: [],
                        totalNumberOfReminders: 3,
                        emailFrequencyOfReminder: 'Daily',
                        reminderTime: 'Morning(09:00 AM)',
                        quoteEmailTemplatePrevPayload: {},
                        quouteEmailReminderPrevPayload: {},
                        isQuoteApprovalOn: false,
                        isQuoteReminderOn: true,
                        expiry: 7,
                    },
                    step3: {
                        showWarning: null,
                        isDisabled: false,
                        isScreenTwo: false,
                        showErrors: {
                            missingValue: null,
                            overlap: null,
                            errorDetails: {
                                lessThan: {},
                                between: [],
                                greaterThan: {},
                            },
                        },
                        dynamicFeesErrors: {
                            missingValue: null,
                            duplicateValue: null,
                            dynamicHiddenFieldError: null,
                            costHiddenFieldError: null,
                            optionError: null,
                            errorDetails: [],
                        },
                        isTransactionInToronto: '',
                        pricingModel: '',
                        flatFeeValue: '',
                        legalFeeDiscount: 0,
                        greaterThanValue: {
                            operatorType: 'greaterThan',
                            value: [1500000],
                            legalFee: 1200,
                        },
                        lessThanValue: {
                            operatorType: 'lessThan',
                            value: [500000],
                            legalFee: 800,
                        },
                        betweenValue: [
                            {
                                operatorType: 'between',
                                value: [500000, 1500000],
                                legalFee: 1000,
                            },
                        ],
                        screenOnePayload: {},
                        isDefaultDisbursementSet: false,
                        isDefaultRegistrationSet: false,
                        disbursementValues: [],
                        disbursementApiValues: [],
                        registrationFeeValues: [],
                        registrationFeeApiValues: [],
                        titleInsuranceEstimation: [],
                    },
                    step4: {
                        apiResponse: null,
                        intakeFormDescription: '',
                        textAfterSubmitIntakeForClient: '',
                        quoteTemplateId: undefined,
                        templateFormId: undefined,
                        templateFormFields: [],
                    },
                    step5: {
                        isScreenTwo: false,
                        subject: INTAKE_EMAIL_BODY_DEFAULTS.Sale.subject,
                        bodyBeforeLink: INTAKE_EMAIL_BODY_DEFAULTS.Sale.bodyBeforeLink,
                        bodyAfterLink: INTAKE_EMAIL_BODY_DEFAULTS.Sale.bodyAfterLink,
                        signatureOfEmail: '',
                        isQuoteApprovalOn: false,
                        intakeEmailTemplateId: null,
                        contactCC: [],
                        intakeEmailStepOnePrevPayload: {},
                        expiry: 180,
                        // intake reminder keys
                        intakeReminderEmailTemplateId: null,
                        reminderSubject: INTAKE_REMINDER_EMAIL_BODY_DEFAULTS.Sale.subject,
                        reminderBodyBeforeLink:
                            INTAKE_REMINDER_EMAIL_BODY_DEFAULTS.Sale.bodyBeforeLink,
                        reminderBodyAfterLink:
                            INTAKE_REMINDER_EMAIL_BODY_DEFAULTS.Sale.bodyAfterLink,
                        reminderSignatureOfEmail: '',
                        reminderContactsCC: [],
                        isQuoteReminderOn: true,
                        intakeEmailTemplatePrevPayload: {},
                        emailFrequencyOfReminder: 'Daily',
                        reminderTime: 'Morning(09:00 AM)',
                        totalNumberOfReminders: 3,
                    },
                };
            }),
        setCcContact: (ccContacts: string[]) => {
            set((state) => {
                state.availableCcContacts = ccContacts;
            });
        },
        setCostEstimationsValues: (
            apiValueKey:
                | 'disbursementApiValues'
                | 'registrationFeeApiValues'
                | 'disbursementValues'
                | 'registrationFeeValues'
                | 'isDefaultDisbursementSet'
                | 'isDefaultRegistrationSet'
                | 'titleInsuranceEstimation'
                | 'titleInsuranceApiValues'
                | 'isDefaultTitleInsuranceSet',
            value: any,
        ) => {
            set((state) => {
                state.formData.step1[apiValueKey] = value;
            });
        },
        setLawyerSignature: async (userId: number, _quoteIntake: string | number | undefined) => {
            try {
                const userResp = await fetchUserById(userId);
                // when you have signature
                if (userResp.data?.signature !== undefined && userResp.data?.signature !== null) {
                    set((state) => {
                        state.formData.step2.signatureOfEmail = userResp.data?.signature ?? '';
                        state.formData.step2.reminderSignatureOfEmail =
                            userResp.data?.signature || '';
                        state.formData.step5.signatureOfEmail = userResp.data?.signature ?? '';
                        state.formData.step5.reminderSignatureOfEmail =
                            userResp.data?.signature || '';
                    });
                } else {
                    set((state) => {
                        state.formData.step2.signatureOfEmail = '';
                        state.formData.step2.reminderSignatureOfEmail = '';
                        state.formData.step5.signatureOfEmail = '';
                        state.formData.step5.reminderSignatureOfEmail = '';
                    });
                }
            } catch (error: any) {
                if (error.status === 404) {
                    set((state) => {
                        state.formData.step0.assignedLawyer = 0;
                    });
                } else {
                    console.error(error);
                }
            }
        },
        resetStep3Errors: () => {
            set((state) => {
                state.formData.step3.dynamicFeesErrors = {
                    dynamicHiddenFieldError: null,
                    costHiddenFieldError: null,
                    optionError: null,
                    missingValue: null,
                    duplicateValue: null,
                    errorDetails: [],
                };
                state.formData.step3.showErrors = {
                    missingValue: null,
                    overlap: null,
                    errorDetails: {
                        lessThan: {},
                        between: [],
                        greaterThan: {},
                    },
                };
            });
        },
    })),
);

export default useQuoteIntakeSetupStore;
